import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarDays, Check, Download, GripVertical, Image as ImageIcon, ImageOff, Info, Link as LinkIcon, Loader2, Lock, Mail, Menu, Monitor, Move, Pencil, Play, Plus, RotateCcw, Send, Smartphone, Trash2, Upload, Video as VideoIcon, X } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ChangeEvent, DragEvent, FormEvent, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Link, UNSAFE_DataRouterContext, useBlocker, useParams } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { ColorSchemePicker } from '../components/ColorSchemePicker';
import { EventDatePickerDialog } from '../components/EventDatePickerDialog';
import { GalleryCover } from '../components/GalleryCover';
import { GalleryGrid } from '../components/GalleryGrid';
import { GalleryStatistics } from '../components/GalleryStatistics';
import { PhotoLoadingPanel, PhotoLoadStatus } from '../components/PhotoLoadStatus';
import { VideoGallery } from '../components/VideoGallery';
import { VideoPosterChooser } from '../components/VideoPosterChooser';
import { Button } from '../components/ui/Button';
import { Dialog, DialogContent } from '../components/ui/Dialog';
import { Field, Input, Textarea } from '../components/ui/Field';
import {
  bulkDeletePhotos,
  checkPhotoDuplicates,
  deletePhoto,
  deleteWatermark,
  getGallery,
  getGalleryStatistics,
  getExport,
  getVideoDownloadUrl,
  listWatermarks,
  listFavouriteSelections,
  renameWatermark,
  retryGalleryWatermark,
  reopenFavouriteSelection,
  createFavouriteSelectionExport,
  shareGalleryByEmail,
  updateGallery,
  updatePhotoScenes,
  uploadPhoto,
  uploadWatermark,
  type ExportJob,
  type CoverFocalPoint,
  type CoverFont,
  type Gallery,
  type FavouriteSelection,
  type GalleryWatermarkConfig,
  type Photo,
  type Video,
  type WatermarkAsset,
  type WatermarkPosition
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { CENTER_COVER_FOCAL_POINT, coverFocalPointStyle, dragCoverFocalPoint, normalizeCoverFocalPoint, nudgeCoverFocalPoint } from '../lib/coverFocalPoint';
import { COVER_FONT_OPTIONS, coverFontPreviewStyle, coverFontVariables, normalizeCoverFont } from '../lib/coverFonts';
import { formatDateOnlyLong } from '../lib/dateOnly';
import {
  GALLERY_STYLE_OPTIONS,
  galleryColorSchemeOf,
  galleryStoryStyleOf,
  normalizeGalleryColorScheme,
  normalizeGalleryStyle,
  type GalleryColorScheme,
  type GalleryStyle
} from '../lib/galleryCustomisation';
import {
  handleMediaError,
  handleMediaLoad,
  useMediaRecovery,
  type MediaErrorHandler
} from '../lib/mediaRecovery';
import { absoluteGalleryUrl, slugify, slugInputValue } from '../lib/utils';
import { inspectVideoFile, uploadVideo, type VideoInspection, type VideoPoster } from '../lib/video';

const PHOTO_PAGE_SIZE = 60;
const ALL_SCENES = '__all__';
const UNASSIGNED_SCENE = '__unassigned__';
const ALL_CLIENTS = '__all_clients__';
const WATERMARK_POSITIONS: WatermarkPosition[] = [
  'top-left', 'top-center', 'top-right',
  'center-left', 'center', 'center-right',
  'bottom-left', 'bottom-center', 'bottom-right'
];
const WATERMARK_POSITION_LABELS: Record<WatermarkPosition, string> = {
  'top-left': 'Top left',
  'top-center': 'Top center',
  'top-right': 'Top right',
  'center-left': 'Center left',
  center: 'Center',
  'center-right': 'Center right',
  'bottom-left': 'Bottom left',
  'bottom-center': 'Bottom center',
  'bottom-right': 'Bottom right'
};
const DEFAULT_WATERMARK_POSITION: WatermarkPosition = 'bottom-right';
const DEFAULT_WATERMARK_SIZE = 40;
const DEFAULT_WATERMARK_OPACITY = 100;
type GallerySection = 'setup' | 'photos' | 'scenes' | 'proofing' | 'share' | 'statistics';

interface UploadState {
  fileName: string;
  current: number;
  total: number;
  percent: number;
}

interface UploadSummary {
  uploadedCount: number;
  duplicates: Array<{ fileName: string; source: 'gallery' | 'batch' }>;
  failed: Array<{ fileName: string; message: string }>;
  cancelled: string[];
}

interface BulkDeleteState {
  completed: number;
  total: number;
}

interface SingleDeleteTarget {
  mediaId: string;
  fileName: string;
  mediaType: 'photo' | 'video';
}

interface PosterRequest {
  file: File;
  inspection: VideoInspection;
}

export function GalleryDetailPage() {
  const { galleryId = '' } = useParams();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [uploadState, setUploadState] = useState<UploadState | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
  const [cancelUploadDialogOpen, setCancelUploadDialogOpen] = useState(false);
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
  const [bulkSelectedPhotoIds, setBulkSelectedPhotoIds] = useState<Set<string>>(() => new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteState, setBulkDeleteState] = useState<BulkDeleteState | null>(null);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const [bulkDeleteFeedback, setBulkDeleteFeedback] = useState<string | null>(null);
  const [locallyDeletedPhotoIds, setLocallyDeletedPhotoIds] = useState<Set<string>>(() => new Set());
  const [locallyDeletedVideoIds, setLocallyDeletedVideoIds] = useState<Set<string>>(() => new Set());
  const [singleDeleteTarget, setSingleDeleteTarget] = useState<SingleDeleteTarget | null>(null);
  const [singleDeleteError, setSingleDeleteError] = useState<string | null>(null);
  const [sceneFilter, setSceneFilter] = useState(UNASSIGNED_SCENE);
  const [proofingClientKey, setProofingClientKey] = useState(ALL_CLIENTS);
  const [proofingSceneFilter, setProofingSceneFilter] = useState(ALL_SCENES);
  const [favouriteExportId, setFavouriteExportId] = useState<string | null>(null);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(() => new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [assignmentScene, setAssignmentScene] = useState('');
  const [assignmentFeedback, setAssignmentFeedback] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<GallerySection>('photos');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showSettingsSaveConfirmation, setShowSettingsSaveConfirmation] = useState(false);
  const [hasUnsavedSetupChanges, setHasUnsavedSetupChanges] = useState(false);
  const [sceneEditorError, setSceneEditorError] = useState<string | null>(null);
  const [coverSaveConfirmation, setCoverSaveConfirmation] = useState<string | null>(null);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [posterRequest, setPosterRequest] = useState<PosterRequest | null>(null);
  const posterResolver = useRef<((poster: VideoPoster | null) => void) | null>(null);
  const uploadController = useRef<AbortController | null>(null);
  const recoverGalleryMedia = useMediaRecovery([
    ['gallery', galleryId],
    ['watermarks']
  ]);

  const galleryQuery = useInfiniteQuery({
    queryKey: ['gallery', galleryId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      getGallery(token!, galleryId, {
        limit: PHOTO_PAGE_SIZE,
        cursor: pageParam
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(token && galleryId)
  });

  const watermarksQuery = useQuery({
    queryKey: ['watermarks'],
    queryFn: () => listWatermarks(token!),
    enabled: Boolean(token)
  });

  const favouriteSelectionsQuery = useQuery({
    queryKey: ['favourite-selections', galleryId],
    queryFn: () => listFavouriteSelections(token!, galleryId),
    enabled: Boolean(token && galleryId)
  });

  const statisticsQuery = useQuery({
    queryKey: ['gallery-statistics', galleryId],
    queryFn: () => getGalleryStatistics(token!, galleryId),
    enabled: Boolean(token && galleryId && activeSection === 'statistics')
  });

  const deleteMutation = useMutation({
    mutationFn: (target: SingleDeleteTarget) => deletePhoto(token!, galleryId, target.mediaId),
    onSuccess: (_, target) => {
      if (target.mediaType === 'photo') {
        setLocallyDeletedPhotoIds((current) => new Set([...current, target.mediaId]));
      } else {
        setLocallyDeletedVideoIds((current) => new Set([...current, target.mediaId]));
      }
      setSingleDeleteTarget(null);
      setSingleDeleteError(null);
      void queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
    },
    onError: (error, target) => {
      setSingleDeleteTarget(null);
      setSingleDeleteError(`Could not delete ${target.fileName}. ${errorMessage(error)}`);
    }
  });

  const coverMutation = useMutation({
    mutationFn: (input: { coverPhotoId: string | null; coverFocalPoint?: CoverFocalPoint }) => updateGallery(token!, galleryId, input),
    onMutate: () => setCoverSaveConfirmation(null),
    onSuccess: (_, input) => {
      setCoverSaveConfirmation(input.coverPhotoId ? 'Cover saved' : 'Cover cleared');
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
    }
  });

  const settingsMutation = useMutation({
    mutationFn: (input: {
      title?: string;
      slug?: string;
      description?: string | null;
      coverFont?: CoverFont;
      customisation?: { colorScheme: GalleryColorScheme; galleryStyle: GalleryStyle };
      eventDate?: string | null;
      passcode?: string;
      isArchived?: boolean;
      favourites?: { limit: number } | null;
      watermark?: GalleryWatermarkConfig | null;
    }) => updateGallery(token!, galleryId, input),
    onSuccess: () => {
      setShowSettingsSaveConfirmation(true);
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
    }
  });

  const uploadWatermarkMutation = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) => uploadWatermark(token!, name, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watermarks'] })
  });

  const renameWatermarkMutation = useMutation({
    mutationFn: ({ watermarkId, name }: { watermarkId: string; name: string }) => renameWatermark(token!, watermarkId, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watermarks'] })
  });

  const deleteWatermarkMutation = useMutation({
    mutationFn: (watermarkId: string) => deleteWatermark(token!, watermarkId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watermarks'] })
  });

  const retryWatermarkMutation = useMutation({
    mutationFn: () => retryGalleryWatermark(token!, galleryId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] })
  });

  const photoSceneMutation = useMutation({
    mutationFn: (input: { photoIds: string[]; scene: string | null }) => updatePhotoScenes(token!, galleryId, input),
    onSuccess: (_, input) => {
      setSelectedPhotoIds(new Set());
      setSelectionMode(false);
      setAssignmentFeedback(
        input.scene
          ? `Assigned ${input.photoIds.length} photo${input.photoIds.length === 1 ? '' : 's'} to ${input.scene}`
          : `Removed scene from ${input.photoIds.length} photo${input.photoIds.length === 1 ? '' : 's'}`
      );
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
    }
  });

  const sceneListMutation = useMutation({
    mutationFn: (nextScenes: string[]) => updateGallery(token!, galleryId, { scenes: nextScenes }),
    onSuccess: () => {
      setSceneEditorError(null);
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
    }
  });

  const reopenFavouriteMutation = useMutation({
    mutationFn: (clientKey: string) => reopenFavouriteSelection(token!, galleryId, clientKey),
    onSuccess: () => {
      setFavouriteExportId(null);
      queryClient.invalidateQueries({ queryKey: ['favourite-selections', galleryId] });
    }
  });

  const favouriteExportMutation = useMutation({
    mutationFn: (clientKey: string) => createFavouriteSelectionExport(token!, galleryId, clientKey),
    onSuccess: ({ exportJob }) => setFavouriteExportId(exportJob.exportId)
  });

  const favouriteExportQuery = useQuery({
    queryKey: ['favourite-selection-export', favouriteExportId],
    queryFn: () => getExport(token!, favouriteExportId!),
    enabled: Boolean(token && favouriteExportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'READY' || status === 'FAILED' ? false : 5000;
    }
  });

  useEffect(() => {
    if (galleryQuery.hasNextPage && !galleryQuery.isFetchingNextPage) {
      void galleryQuery.fetchNextPage();
    }
  }, [galleryQuery.fetchNextPage, galleryQuery.hasNextPage, galleryQuery.isFetchingNextPage]);

  useEffect(() => {
    if (!showSettingsSaveConfirmation) {
      return;
    }
    const timeoutId = window.setTimeout(() => setShowSettingsSaveConfirmation(false), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [showSettingsSaveConfirmation]);

  useEffect(() => {
    if (!coverSaveConfirmation) {
      return;
    }
    const timeoutId = window.setTimeout(() => setCoverSaveConfirmation(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [coverSaveConfirmation]);

  const photoPages = galleryQuery.data?.pages ?? [];
  const gallery = photoPages[0]?.gallery;
  const photos = photoPages.flatMap((page) => page.photos).filter((photo) => !locallyDeletedPhotoIds.has(photo.photoId));
  const videos = (photoPages[0]?.videos ?? []).filter((video) => !locallyDeletedVideoIds.has(video.photoId));
  const scenes = gallery?.scenes ?? [];
  const shareUrl = gallery ? absoluteGalleryUrl(gallery.photographerSlug, gallery.gallerySlug) : '';
  const photosLoading = galleryQuery.isLoading || galleryQuery.isFetchingNextPage;
  const allPhotosLoaded = Boolean(gallery && !galleryQuery.hasNextPage && !galleryQuery.isFetchingNextPage);
  const favouriteSelections = favouriteSelectionsQuery.data?.selections ?? [];
  const filteredPhotos = useMemo(() => filterPhotosByScene(photos, sceneFilter), [photos, sceneFilter]);
  const selectedProofing = useMemo(
    () => favouriteSelections.find((selection) => selection.clientKey === proofingClientKey),
    [favouriteSelections, proofingClientKey]
  );
  const proofingPhotos = useMemo(() => {
    if (!selectedProofing) return [];
    const selectedIds = new Set(selectedProofing.photoIds);
    return filterPhotosByScene(
      photos.filter((photo) => selectedIds.has(photo.photoId)),
      proofingSceneFilter
    );
  }, [photos, proofingSceneFilter, selectedProofing]);

  useEffect(() => {
    if (scenes.length === 0) {
      if (sceneFilter !== UNASSIGNED_SCENE) {
        setSceneFilter(UNASSIGNED_SCENE);
      }
      return;
    }
    if (sceneFilter === UNASSIGNED_SCENE) {
      return;
    }
    if (!scenes.includes(sceneFilter)) {
      setSceneFilter(scenes[0] ?? UNASSIGNED_SCENE);
    }
  }, [sceneFilter, scenes]);

  useEffect(() => {
    if (proofingClientKey !== ALL_CLIENTS && !favouriteSelections.some((selection) => selection.clientKey === proofingClientKey)) {
      setProofingClientKey(ALL_CLIENTS);
      setFavouriteExportId(null);
    }
  }, [proofingClientKey, favouriteSelections]);

  useEffect(() => {
    if (proofingSceneFilter !== ALL_SCENES && proofingSceneFilter !== UNASSIGNED_SCENE && !scenes.includes(proofingSceneFilter)) {
      setProofingSceneFilter(ALL_SCENES);
    }
  }, [proofingSceneFilter, scenes]);

  useEffect(() => {
    if (sceneFilter !== UNASSIGNED_SCENE && scenes.includes(sceneFilter)) {
      setAssignmentScene(sceneFilter);
      return;
    }
    setAssignmentScene('');
  }, [sceneFilter, scenes]);

  useEffect(() => {
    if (!assignmentFeedback) {
      return;
    }
    const timeoutId = window.setTimeout(() => setAssignmentFeedback(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [assignmentFeedback]);

  useEffect(() => {
    const loadedPhotoIds = new Set(photos.map((photo) => photo.photoId));
    setSelectedPhotoIds((current) => {
      const next = new Set([...current].filter((photoId) => loadedPhotoIds.has(photoId)));
      return next.size === current.size ? current : next;
    });
  }, [photos]);

  useEffect(() => {
    const loadedPhotoIds = new Set(photos.map((photo) => photo.photoId));
    setBulkSelectedPhotoIds((current) => {
      const next = new Set([...current].filter((photoId) => loadedPhotoIds.has(photoId)));
      return next.size === current.size ? current : next;
    });
  }, [photos]);

  useEffect(() => {
    if (!uploadState) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadState]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileNavOpen]);

  async function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    const input = event.currentTarget;
    if (files.length === 0 || !token) {
      return;
    }
    setUploadError(null);
    setUploadSummary(null);
    const controller = new AbortController();
    uploadController.current = controller;
    setUploadState({ fileName: files[0].name, current: 1, total: files.length, percent: 0 });
    const summary: UploadSummary = { uploadedCount: 0, duplicates: [], failed: [], cancelled: [] };
    try {
      const imageFiles = files.filter(isImageFile);
      let duplicateImageIndexes = new Map<number, 'gallery' | 'batch'>();
      try {
        const result = await checkPhotoDuplicates(token, galleryId, imageFiles.map((file) => file.name), controller.signal);
        duplicateImageIndexes = new Map(result.duplicates.map((duplicate) => [duplicate.index, duplicate.source]));
        summary.duplicates.push(...result.duplicates.map((duplicate) => ({ fileName: duplicate.fileName, source: duplicate.source })));
      } catch (error) {
        if (isAbortError(error)) {
          summary.cancelled.push(...files.map((file) => file.name));
          setUploadSummary(summary);
          return;
        }
        setUploadError(`No files were uploaded because duplicate checking failed. ${errorMessage(error)}`);
        return;
      }

      let imageIndex = 0;
      for (const [index, file] of files.entries()) {
        const currentImageIndex = isImageFile(file) ? imageIndex++ : -1;
        const duplicateSource = currentImageIndex >= 0 ? duplicateImageIndexes.get(currentImageIndex) : undefined;
        if (duplicateSource) {
          setUploadState({ fileName: file.name, current: index + 1, total: files.length, percent: Math.round(((index + 1) / files.length) * 100) });
          continue;
        }
        if (controller.signal.aborted) {
          summary.cancelled.push(...files.slice(index).filter((candidate) => {
            if (!isImageFile(candidate)) return true;
            const candidateImageIndex = imageFiles.indexOf(candidate);
            return !duplicateImageIndexes.has(candidateImageIndex);
          }).map((candidate) => candidate.name));
          break;
        }
        const onProgress = ({ percent }: { percent: number }) => {
          setUploadState({
            fileName: file.name,
            current: index + 1,
            total: files.length,
            percent: Math.round(((index + percent / 100) / files.length) * 100)
          });
        };
        try {
          if (isImageFile(file)) {
            await uploadPhoto(token, galleryId, file, { signal: controller.signal, onProgress });
            summary.uploadedCount += 1;
            continue;
          }
          if (!isVideoCandidate(file)) {
            throw new Error('Only JPEG, PNG, WebP, and browser-ready MP4 files are supported.');
          }
          onProgress({ percent: 1 });
          const inspection = await inspectVideoFile(file, controller.signal);
          const poster = await requestPoster(file, inspection, controller.signal);
          if (!poster) {
            summary.cancelled.push(file.name);
            continue;
          }
          await uploadVideo(token, galleryId, file, inspection, poster, {
            signal: controller.signal,
            onProgress
          });
          summary.uploadedCount += 1;
        } catch (error) {
          if (isAbortError(error) || controller.signal.aborted) {
            summary.cancelled.push(file.name);
            summary.cancelled.push(...files.slice(index + 1).filter((candidate) => {
              if (!isImageFile(candidate)) return true;
              const candidateImageIndex = imageFiles.indexOf(candidate);
              return !duplicateImageIndexes.has(candidateImageIndex);
            }).map((candidate) => candidate.name));
            break;
          }
          summary.failed.push({ fileName: file.name, message: errorMessage(error) });
          continue;
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
      setUploadSummary(summary);
    } finally {
      uploadController.current = null;
      setUploadState(null);
      setCancelUploadDialogOpen(false);
      input.value = '';
    }
  }

  function requestPoster(file: File, inspection: VideoInspection, signal: AbortSignal): Promise<VideoPoster | null> {
    return new Promise((resolve, reject) => {
      const handleAbort = () => {
        posterResolver.current = null;
        setPosterRequest(null);
        reject(new DOMException('Video upload was cancelled.', 'AbortError'));
      };
      posterResolver.current = (poster) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(poster);
      };
      signal.addEventListener('abort', handleAbort, { once: true });
      if (signal.aborted) {
        handleAbort();
        return;
      }
      setPosterRequest({ file, inspection });
    });
  }

  function resolvePoster(poster: VideoPoster | null) {
    const resolve = posterResolver.current;
    posterResolver.current = null;
    setPosterRequest(null);
    resolve?.(poster);
  }

  function toggleBulkPhoto(photoId: string) {
    setBulkSelectedPhotoIds((current) => {
      const next = new Set(current);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
    setBulkDeleteError(null);
    setBulkDeleteFeedback(null);
  }

  function requestSingleDelete(target: SingleDeleteTarget) {
    setSingleDeleteError(null);
    setSingleDeleteTarget(target);
  }

  function confirmSingleDelete() {
    if (!singleDeleteTarget || deleteMutation.isPending) {
      return;
    }
    deleteMutation.mutate(singleDeleteTarget);
  }

  async function performBulkDelete(photoIds: string[]) {
    if (!token || photoIds.length === 0) {
      return;
    }
    setBulkDeleteDialogOpen(false);
    setBulkDeleteError(null);
    setBulkDeleteFeedback(null);
    setBulkDeleteState({ completed: 0, total: photoIds.length });
    let completed = 0;
    try {
      for (let offset = 0; offset < photoIds.length; offset += 50) {
        const chunk = photoIds.slice(offset, offset + 50);
        const result = await bulkDeletePhotos(token, galleryId, chunk);
        const removedIds = [...result.deletedPhotoIds, ...result.alreadyMissingPhotoIds];
        completed += chunk.length;
        setLocallyDeletedPhotoIds((current) => new Set([...current, ...removedIds]));
        setBulkSelectedPhotoIds((current) => {
          const next = new Set(current);
          removedIds.forEach((photoId) => next.delete(photoId));
          return next;
        });
        setBulkDeleteState({ completed, total: photoIds.length });
      }
      setBulkSelectionMode(false);
      setBulkSelectedPhotoIds(new Set());
      setBulkDeleteError(null);
      setBulkDeleteFeedback(null);
    } catch (error) {
      setBulkDeleteError(`${completed} of ${photoIds.length} processed. ${errorMessage(error)}`);
    } finally {
      setBulkDeleteState(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] }),
        queryClient.invalidateQueries({ queryKey: ['favourite-selections', galleryId] })
      ]);
    }
  }

  async function downloadVideo(video: Video) {
    if (!token) {
      return;
    }
    try {
      const { url } = await getVideoDownloadUrl(token, galleryId, video.photoId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Could not prepare the video download.');
    }
  }

  const uploading = Boolean(uploadState);

  function handleSectionChange(section: GallerySection) {
    if (section !== activeSection && activeSection === 'setup' && hasUnsavedSetupChanges) {
      const shouldLeave = window.confirm('You have unsaved gallery setup changes. Leave this page without saving?');
      if (!shouldLeave) {
        return;
      }
    }
    if (section === 'scenes') {
      setSceneFilter((current) => {
        if (scenes.length === 0) {
          return UNASSIGNED_SCENE;
        }
        return current !== UNASSIGNED_SCENE && scenes.includes(current) ? current : scenes[0];
      });
    }
    setActiveSection(section);
    setMobileNavOpen(false);
  }

  return (
    <main className="min-h-screen bg-white text-ink lg:grid lg:grid-cols-[390px_minmax(0,1fr)]">
      <MaybeUploadNavigationGuard
        active={uploading}
        onLeave={() => uploadController.current?.abort()}
      />
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-ink/10 bg-smoke/95 px-5 py-4 backdrop-blur lg:hidden">
        <div className="flex min-w-0 items-center gap-3">
          <BrandLogo variant="mark" className="h-11 w-11 shrink-0 object-contain" />
          <div className="min-w-0">
            <p className="truncate text-sm text-ink/70">Gallery workspace</p>
            <p className="truncate text-base font-medium text-ink">{gallery?.title ?? 'Loading'}</p>
          </div>
        </div>
        <Button type="button" variant="secondary" size="icon" onClick={() => setMobileNavOpen(true)} aria-label="Open gallery navigation">
          <Menu className="h-5 w-5" />
        </Button>
      </header>

      <MobileGalleryNav
        open={mobileNavOpen}
        title={gallery?.title ?? 'Loading'}
        activeSection={activeSection}
        shareUrl={shareUrl}
        onClose={() => setMobileNavOpen(false)}
        onSectionChange={handleSectionChange}
      />

      <aside className="hidden bg-smoke/70 lg:block lg:min-h-screen">
        <div className="flex min-h-full flex-col gap-8 px-8 py-8 sm:px-12 lg:sticky lg:top-0 lg:h-screen lg:px-20 lg:py-12">
          <BrandLogo variant="mark" className="h-16 w-16 object-contain" />
          <div className="grid gap-6">
            <Link to="/dashboard" className="inline-flex w-fit items-center gap-2 text-sm text-ink transition hover:text-ink/70">
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Link>
            <h1 className="break-words text-3xl font-medium tracking-normal text-ink">{gallery?.title ?? 'Loading'}</h1>
          </div>
          <nav className="grid gap-4 text-left text-2xl font-normal">
            <GalleryNavButton active={activeSection === 'setup'} onClick={() => handleSectionChange('setup')}>
              Gallery Setup
            </GalleryNavButton>
            <GalleryNavButton active={activeSection === 'photos'} onClick={() => handleSectionChange('photos')}>
              Photos & Media
            </GalleryNavButton>
            <GalleryNavButton active={activeSection === 'scenes'} onClick={() => handleSectionChange('scenes')}>
              Scenes
            </GalleryNavButton>
            <GalleryNavButton active={activeSection === 'proofing'} onClick={() => handleSectionChange('proofing')}>
              Client Proofing
            </GalleryNavButton>
            <GalleryNavButton active={activeSection === 'share'} onClick={() => handleSectionChange('share')}>
              Share Gallery
            </GalleryNavButton>
            <GalleryNavButton active={activeSection === 'statistics'} onClick={() => handleSectionChange('statistics')}>
              Gallery Statistics
            </GalleryNavButton>
          </nav>
          <div className="mt-auto pt-4">
            {shareUrl ? (
              <Button asChild className="w-full max-w-44">
                <a href={shareUrl} target="_blank" rel="noreferrer">
                  Preview Gallery
                </a>
              </Button>
            ) : (
              <Button disabled className="w-full max-w-44">
                Preview Gallery
              </Button>
            )}
          </div>
        </div>
      </aside>

      <div className="min-w-0 px-5 py-8 sm:px-8 lg:px-16 lg:py-14">
        <div className="mx-auto max-w-[1180px]">
          {activeSection === 'setup' ? (
            gallery ? (
              <GallerySettings
                gallery={gallery}
                photos={photos}
                videos={videos}
                watermarks={watermarksQuery.data?.watermarks ?? []}
                isSaving={settingsMutation.isPending}
                isUpdatingCover={coverMutation.isPending}
                coverError={coverMutation.error}
                showSaveConfirmation={showSettingsSaveConfirmation}
                error={settingsMutation.error}
                watermarkError={
                  uploadWatermarkMutation.error ?? renameWatermarkMutation.error ?? deleteWatermarkMutation.error ?? retryWatermarkMutation.error
                }
                isManagingWatermarks={
                  uploadWatermarkMutation.isPending || renameWatermarkMutation.isPending || deleteWatermarkMutation.isPending
                }
                onDirtyChange={setHasUnsavedSetupChanges}
                onChooseCover={() => setCoverPickerOpen(true)}
                onSettingsChange={() => setShowSettingsSaveConfirmation(false)}
                onSave={(input) => settingsMutation.mutate(input)}
                onUploadWatermark={(name, file) => uploadWatermarkMutation.mutate({ name, file })}
                onRenameWatermark={(watermarkId, name) => renameWatermarkMutation.mutate({ watermarkId, name })}
                onDeleteWatermark={(watermarkId) => deleteWatermarkMutation.mutate(watermarkId)}
                onRetryWatermark={() => retryWatermarkMutation.mutate()}
                onMediaError={recoverGalleryMedia}
              />
            ) : (
              <LoadingSection label="Loading gallery setup" />
            )
          ) : null}

          {activeSection === 'photos' ? (
            <section className="grid gap-7">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-2xl font-medium tracking-normal text-ink">Gallery</h2>
                <div className="flex flex-wrap items-center gap-3">
                  {bulkSelectionMode ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="min-h-11"
                      onClick={() => {
                        setBulkSelectionMode(false);
                        setBulkSelectedPhotoIds(new Set());
                        setBulkDeleteError(null);
                        setBulkDeleteFeedback(null);
                      }}
                      disabled={Boolean(bulkDeleteState)}
                    >
                      Done
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="min-h-11"
                      onClick={() => {
                        setBulkSelectionMode(true);
                        setBulkDeleteError(null);
                        setBulkDeleteFeedback(null);
                      }}
                      disabled={photos.length === 0}
                    >
                      Bulk select
                    </Button>
                  )}
                  <label className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm px-4 text-sm font-medium text-ink/70 transition hover:text-ink">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    <span>{uploading ? 'Uploading' : 'Add Media'}</span>
                    <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp,video/mp4,.mp4" multiple onChange={(event) => void handleFiles(event)} disabled={uploading} />
                  </label>
                </div>
              </div>

              {uploadState ? (
                <div className="rounded-sm border border-ink/10 bg-bone/55 p-4">
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                    <p className="min-w-0 truncate font-medium text-ink">
                      Uploading {uploadState.current}/{uploadState.total}: {uploadState.fileName}
                    </p>
                    <span className="shrink-0 text-ink/70">{uploadState.percent}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white">
                    <div className="h-full rounded-full bg-sea transition-all duration-300" style={{ width: `${uploadState.percent}%` }} />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={() => setCancelUploadDialogOpen(true)}>Cancel upload</Button>
                  </div>
                </div>
              ) : null}

              {uploadError ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{uploadError}</p> : null}
              {singleDeleteError ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay" role="alert">{singleDeleteError}</p> : null}
              {uploadSummary ? <UploadSummaryPanel summary={uploadSummary} onDismiss={() => setUploadSummary(null)} /> : null}

              {videos.length > 0 ? (
                <section className="grid gap-4" aria-labelledby="gallery-videos-heading">
                  <div>
                    <h3 id="gallery-videos-heading" className="text-xl font-medium text-ink">Videos</h3>
                    <p className="mt-1 text-sm text-ink/70">Videos stay separate from photo scenes.</p>
                  </div>
                  <VideoGallery
                    videos={videos}
                    deletingVideoId={deleteMutation.isPending && deleteMutation.variables?.mediaType === 'video' ? deleteMutation.variables.mediaId : null}
                    onDelete={bulkSelectionMode ? undefined : (videoId) => {
                      const video = videos.find((item) => item.photoId === videoId);
                      if (video) requestSingleDelete({ mediaId: video.photoId, fileName: video.fileName, mediaType: 'video' });
                    }}
                    onDownload={(video) => void downloadVideo(video)}
                    onMediaError={recoverGalleryMedia}
                  />
                </section>
              ) : null}

              <div className="grid gap-3">
                <h3 className="text-xl font-medium text-ink">Photos</h3>
                <div className="rounded-sm border border-ink/10 bg-white p-3">
                  <PhotoLoadStatus loadedCount={photos.length} expectedCount={gallery ? imageCountOf(gallery) : undefined} isLoading={photosLoading} />
                </div>
                {bulkSelectionMode ? (
                  <div className="grid gap-3 rounded-sm border border-sea/25 bg-sea/5 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-sm bg-sea/10 px-2.5 py-1 text-xs font-medium text-sea">{bulkSelectedPhotoIds.size} selected</span>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="min-h-11"
                        onClick={() => setBulkSelectedPhotoIds(new Set(photos.map((photo) => photo.photoId)))}
                        disabled={!allPhotosLoaded || photos.length === 0 || Boolean(bulkDeleteState)}
                        title={!allPhotosLoaded ? 'Available after every photo has loaded' : undefined}
                      >
                        Select all
                      </Button>
                      <Button type="button" variant="secondary" size="sm" className="min-h-11" onClick={() => setBulkSelectedPhotoIds(new Set())} disabled={bulkSelectedPhotoIds.size === 0 || Boolean(bulkDeleteState)}>
                        Clear selected
                      </Button>
                      <Button type="button" size="sm" className="min-h-11" onClick={() => setBulkDeleteDialogOpen(true)} disabled={bulkSelectedPhotoIds.size === 0 || Boolean(bulkDeleteState)}>
                        <Trash2 className="h-4 w-4" /> Delete selected
                      </Button>
                      {bulkDeleteError && bulkSelectedPhotoIds.size > 0 ? (
                        <Button type="button" variant="secondary" size="sm" className="min-h-11" onClick={() => void performBulkDelete([...bulkSelectedPhotoIds])}>
                          Retry remaining
                        </Button>
                      ) : null}
                    </div>
                    {!allPhotosLoaded ? <p className="text-xs text-ink/70">Select all becomes available after every photo has loaded.</p> : null}
                    {bulkDeleteState ? (
                      <div className="grid gap-2" role="status" aria-live="polite">
                        <div className="flex justify-between gap-3 text-sm text-ink/70">
                          <span>Deleting selected photos</span><span>{bulkDeleteState.completed}/{bulkDeleteState.total}</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-white">
                          <div className="h-full rounded-full bg-sea transition-all" style={{ width: `${(bulkDeleteState.completed / bulkDeleteState.total) * 100}%` }} />
                        </div>
                      </div>
                    ) : null}
                    {bulkDeleteError ? <p className="text-sm text-clay">{bulkDeleteError} Failed and unprocessed photos remain selected.</p> : null}
                    {bulkDeleteFeedback ? <p className="text-sm font-medium text-sea">{bulkDeleteFeedback}</p> : null}
                  </div>
                ) : null}
              </div>

              {photos.length === 0 && photosLoading ? (
                <PhotoLoadingPanel loadedCount={photos.length} expectedCount={gallery ? imageCountOf(gallery) : undefined} />
              ) : photos.length === 0 ? (
                <SceneEmptyState
                  sceneFilter=""
                  showAddMedia
                  isUploading={uploading}
                  onFilesSelected={(event) => void handleFiles(event)}
                />
              ) : (
                <GalleryGrid
                  photos={photos}
                  canDelete={!bulkSelectionMode}
                  deletingPhotoId={deleteMutation.isPending && deleteMutation.variables?.mediaType === 'photo' ? deleteMutation.variables.mediaId : null}
                  onDelete={bulkSelectionMode ? undefined : (photoId) => {
                    const photo = photos.find((item) => item.photoId === photoId);
                    if (photo) requestSingleDelete({ mediaId: photo.photoId, fileName: photo.fileName, mediaType: 'photo' });
                  }}
                  selectedPhotoIds={bulkSelectionMode ? bulkSelectedPhotoIds : undefined}
                  onToggleSelected={bulkSelectionMode ? toggleBulkPhoto : undefined}
                  onMediaError={recoverGalleryMedia}
                />
              )}
            </section>
          ) : null}

          {activeSection === 'scenes' ? (
            gallery ? (
              <ScenesSection
                gallery={gallery}
                photos={photos}
                filteredPhotos={filteredPhotos}
                scenes={scenes}
                sceneFilter={sceneFilter}
                selectedPhotoIds={selectedPhotoIds}
                selectedCount={selectedPhotoIds.size}
                selectionMode={selectionMode}
                assignmentScene={assignmentScene}
                isSavingScenes={sceneListMutation.isPending}
                isAssigning={photoSceneMutation.isPending}
                saveError={sceneEditorError}
                assignmentError={photoSceneMutation.error}
                assignmentFeedback={assignmentFeedback}
                onFilterChange={(nextFilter) => {
                  setSceneFilter(nextFilter);
                  setSelectedPhotoIds(new Set());
                  setSelectionMode(false);
                  setAssignmentFeedback(null);
                }}
                onAssignmentSceneChange={setAssignmentScene}
                onStartSelection={() => {
                  setAssignmentFeedback(null);
                  setSelectionMode(true);
                }}
                onCancelSelection={() => {
                  setSelectedPhotoIds(new Set());
                  setSelectionMode(false);
                  setAssignmentFeedback(null);
                }}
                onSelectAllVisible={() => {
                  setAssignmentFeedback(null);
                  setSelectionMode(true);
                  setSelectedPhotoIds((current) => {
                    const next = new Set(current);
                    filteredPhotos.forEach((photo) => next.add(photo.photoId));
                    return next;
                  });
                }}
                onClearSelection={() => {
                  setSelectedPhotoIds(new Set());
                  setAssignmentFeedback(null);
                }}
                onToggleSelected={(photoId) =>
                  setSelectedPhotoIds((current) => {
                    setAssignmentFeedback(null);
                    const next = new Set(current);
                    if (next.has(photoId)) {
                      next.delete(photoId);
                    } else {
                      next.add(photoId);
                    }
                    return next;
                  })
                }
                onAssignScene={() => {
                  if (!assignmentScene) {
                    return;
                  }
                  photoSceneMutation.mutate({ photoIds: [...selectedPhotoIds], scene: assignmentScene });
                }}
                onClearScene={() => photoSceneMutation.mutate({ photoIds: [...selectedPhotoIds], scene: null })}
                onSaveScenes={(nextScenes) => {
                  if (!gallery) {
                    return;
                  }
                  const previousScenes = gallery.scenes ?? [];
                  const galleryQueryKey = ['gallery', galleryId] as const;
                  setSceneEditorError(null);
                  void queryClient.cancelQueries({ queryKey: galleryQueryKey });
                  queryClient.setQueryData(galleryQueryKey, (current: typeof galleryQuery.data) => {
                    if (!current) return current;
                    return {
                      ...current,
                      pages: current.pages.map((page, index) =>
                        index === 0 ? { ...page, gallery: { ...page.gallery, scenes: nextScenes } } : page
                      )
                    };
                  });
                  sceneListMutation.mutate(nextScenes, {
                    onError: (error) => {
                      queryClient.setQueryData(galleryQueryKey, (current: typeof galleryQuery.data) => {
                        if (!current) return current;
                        return {
                          ...current,
                          pages: current.pages.map((page, index) =>
                            index === 0 ? { ...page, gallery: { ...page.gallery, scenes: previousScenes } } : page
                          )
                        };
                      });
                      setSceneEditorError(error.message);
                    }
                  });
                }}
                onMediaError={recoverGalleryMedia}
              />
            ) : (
              <LoadingSection label="Loading gallery scenes" />
            )
          ) : null}

          {activeSection === 'proofing' ? (
            gallery ? (
              <ProofingSection
                gallery={gallery}
                scenes={scenes}
                photos={photos}
                filteredPhotos={proofingPhotos}
                selectedClientKey={proofingClientKey}
                selectedSelection={selectedProofing}
                sceneFilter={proofingSceneFilter}
                isSaving={settingsMutation.isPending}
                isLoadingSelections={favouriteSelectionsQuery.isLoading}
                isReopening={reopenFavouriteMutation.isPending}
                isExporting={favouriteExportMutation.isPending}
                exportJob={favouriteExportQuery.data}
                error={
                  settingsMutation.error ??
                  favouriteSelectionsQuery.error ??
                  reopenFavouriteMutation.error ??
                  favouriteExportMutation.error ??
                  favouriteExportQuery.error
                }
                onSave={(favourites) => settingsMutation.mutate({ favourites })}
                onClientChange={(clientKey) => {
                  setProofingClientKey(clientKey);
                  setFavouriteExportId(null);
                }}
                onSceneFilterChange={setProofingSceneFilter}
                onReopen={(clientKey) => reopenFavouriteMutation.mutate(clientKey)}
                onExport={(clientKey) => favouriteExportMutation.mutate(clientKey)}
                selections={favouriteSelections}
                onMediaError={recoverGalleryMedia}
              />
            ) : (
              <LoadingSection label="Loading client proofing" />
            )
          ) : null}

          {activeSection === 'share' ? (
            gallery ? <ShareGallerySection gallery={gallery} shareUrl={shareUrl} /> : <LoadingSection label="Loading gallery sharing" />
          ) : null}

          {activeSection === 'statistics' ? (
            galleryQuery.error || favouriteSelectionsQuery.error || statisticsQuery.error ? (
              <section className="rounded-sm border border-red-200 bg-red-50 p-6" role="alert">
                <h2 className="text-2xl font-medium text-ink">Gallery statistics unavailable</h2>
                <p className="mt-2 text-sm text-red-800">We couldn’t load this gallery’s statistics. Please refresh and try again.</p>
              </section>
            ) : !gallery || photosLoading || favouriteSelectionsQuery.isLoading || statisticsQuery.isLoading ? (
              <LoadingSection label="Loading gallery statistics" />
            ) : statisticsQuery.data ? (
              <GalleryStatistics
                gallery={gallery}
                photos={photos}
                videos={videos}
                selections={favouriteSelections}
                statistics={statisticsQuery.data}
                onMediaError={recoverGalleryMedia}
              />
            ) : null
          ) : null}
        </div>
      </div>
      {posterRequest ? (
        <VideoPosterChooser
          file={posterRequest.file}
          inspection={posterRequest.inspection}
          signal={uploadController.current?.signal}
          onCancel={() => resolvePoster(null)}
          onConfirm={resolvePoster}
        />
      ) : null}
      {gallery ? (
        <GalleryCoverPicker
          open={coverPickerOpen}
          gallery={gallery}
          photos={photos}
          videos={videos}
          isSaving={coverMutation.isPending}
          error={coverMutation.error}
          onClose={() => setCoverPickerOpen(false)}
          onSelectCover={(photoId, coverFocalPoint) =>
            coverMutation.mutate({ coverPhotoId: photoId, coverFocalPoint }, {
              onSuccess: () => setCoverPickerOpen(false)
            })
          }
          onClearCover={() =>
            coverMutation.mutate({ coverPhotoId: null }, {
              onSuccess: () => setCoverPickerOpen(false)
            })
          }
          onMediaError={recoverGalleryMedia}
        />
      ) : null}
      {coverSaveConfirmation ? (
        <p
          className="fixed bottom-5 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-ink/70 shadow-soft ring-1 ring-ink/10 backdrop-blur"
          role="status"
          aria-live="polite"
        >
          <Check className="h-4 w-4 text-sea" aria-hidden="true" />
          <span>{coverSaveConfirmation}</span>
        </p>
      ) : null}
      <Dialog open={cancelUploadDialogOpen} onOpenChange={setCancelUploadDialogOpen}>
        <DialogContent
          title="Cancel this upload batch?"
          description="The active upload and every remaining file will be cancelled. Files that already finished will stay in the gallery."
        >
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={() => setCancelUploadDialogOpen(false)}>Keep uploading</Button>
            <Button
              type="button"
              onClick={() => {
                setCancelUploadDialogOpen(false);
                uploadController.current?.abort();
              }}
            >
              Cancel upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent
          title="Permanently delete selected photos?"
          description={`${bulkSelectedPhotoIds.size} selected photo${bulkSelectedPhotoIds.size === 1 ? '' : 's'} will be removed from this gallery and cannot be recovered.`}
        >
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={() => setBulkDeleteDialogOpen(false)}>Keep photos</Button>
            <Button type="button" onClick={() => void performBulkDelete([...bulkSelectedPhotoIds])} disabled={bulkSelectedPhotoIds.size === 0}>
              <Trash2 className="h-4 w-4" /> Delete permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(singleDeleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setSingleDeleteTarget(null);
          }
        }}
      >
        <DialogContent
          title={singleDeleteTarget ? `Delete this ${singleDeleteTarget.mediaType}?` : 'Delete this media?'}
          description={singleDeleteTarget ? `“${singleDeleteTarget.fileName}” and its stored media will be permanently deleted. This cannot be undone.` : undefined}
        >
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={() => setSingleDeleteTarget(null)} disabled={deleteMutation.isPending}>
              Keep {singleDeleteTarget?.mediaType ?? 'media'}
            </Button>
            <Button type="button" variant="danger" onClick={confirmSingleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleteMutation.isPending ? 'Deleting…' : `Delete ${singleDeleteTarget?.mediaType ?? 'media'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function MaybeUploadNavigationGuard({ active, onLeave }: { active: boolean; onLeave: () => void }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  return dataRouter ? <UploadNavigationGuard active={active} onLeave={onLeave} /> : null;
}

function UploadNavigationGuard({ active, onLeave }: { active: boolean; onLeave: () => void }) {
  const blocker = useBlocker(active);
  const blocked = blocker.state === 'blocked';
  return (
    <Dialog
      open={blocked}
      onOpenChange={(open) => {
        if (!open && blocker.state === 'blocked') blocker.reset();
      }}
    >
      <DialogContent
        title="An upload is still in progress"
        description="Stay here to let it finish, or leave and cancel the active file and everything still waiting in this batch. Completed files will remain."
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={() => blocker.state === 'blocked' && blocker.reset()}>Stay</Button>
          <Button
            type="button"
            onClick={() => {
              onLeave();
              if (blocker.state === 'blocked') blocker.proceed();
            }}
          >
            Leave and cancel upload
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UploadSummaryPanel({ summary, onDismiss }: { summary: UploadSummary; onDismiss: () => void }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasIssues = summary.duplicates.length > 0 || summary.failed.length > 0 || summary.cancelled.length > 0;

  return (
    <>
      <section
        className="rounded-sm border border-ink/10 bg-white p-4 shadow-sm"
        aria-labelledby="upload-summary-heading"
        aria-live="polite"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h3 id="upload-summary-heading" className="font-semibold text-ink">Upload results</h3>
            <p className="mt-1 text-sm text-ink/70">
              {summary.uploadedCount} uploaded · {summary.duplicates.length} duplicate{summary.duplicates.length === 1 ? '' : 's'} · {summary.failed.length} failed · {summary.cancelled.length} cancelled
            </p>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-1">
            {hasIssues ? (
              <Button type="button" variant="secondary" size="sm" onClick={() => setDetailsOpen(true)}>
                <Info className="h-4 w-4" aria-hidden="true" />
                <span>View info</span>
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="icon" onClick={onDismiss} aria-label="Dismiss upload results">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent
          title="Upload details"
          description="Review files that were skipped, failed, or cancelled."
          className="max-w-2xl"
        >
          <div className="grid gap-5">
            {summary.duplicates.length > 0 ? (
              <UploadResultList
                label={`Skipped duplicates (${summary.duplicates.length})`}
                items={summary.duplicates.map((duplicate) => `${duplicate.fileName} (${duplicate.source === 'gallery' ? 'already in gallery' : 'repeated in this batch'})`)}
              />
            ) : null}
            {summary.failed.length > 0 ? (
              <UploadResultList
                label={`Failed (${summary.failed.length})`}
                items={summary.failed.map((failure) => `${failure.fileName}: ${failure.message}`)}
                className="text-clay"
              />
            ) : null}
            {summary.cancelled.length > 0 ? (
              <UploadResultList label={`Cancelled (${summary.cancelled.length})`} items={summary.cancelled} />
            ) : null}
            <div className="flex justify-end border-t border-ink/10 pt-4">
              <Button type="button" variant="secondary" onClick={() => setDetailsOpen(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UploadResultList({ label, items, className = 'text-ink/75' }: { label: string; items: string[]; className?: string }) {
  return (
    <div>
      <p className={`font-medium ${className}`}>{label}</p>
      <ul className="mt-2 grid gap-2 text-sm text-ink/70">
        {items.map((item, index) => (
          <li key={`${index}-${item}`} className="break-words rounded-sm bg-bone/55 px-3 py-2">{item}</li>
        ))}
      </ul>
    </div>
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The request failed.';
}

function isImageFile(file: File): boolean {
  return ['image/jpeg', 'image/png', 'image/webp'].includes(file.type.toLowerCase());
}

function isVideoCandidate(file: File): boolean {
  return file.type.toLowerCase().startsWith('video/') || /\.(mp4|mov)$/i.test(file.name);
}

function imageCountOf(gallery: Gallery): number {
  return gallery.imageCount ?? Math.max(0, gallery.photoCount - (gallery.videoCount ?? 0));
}

function MobileGalleryNav({
  open,
  title,
  activeSection,
  shareUrl,
  onClose,
  onSectionChange
}: {
  open: boolean;
  title: string;
  activeSection: GallerySection;
  shareUrl: string;
  onClose: () => void;
  onSectionChange: (section: GallerySection) => void;
}) {
  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Gallery navigation">
      <button type="button" className="absolute inset-0 bg-ink/35" onClick={onClose} aria-label="Close gallery navigation overlay" />
      <div className="relative flex h-full w-[min(84vw,340px)] flex-col gap-8 bg-smoke px-8 py-8 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <BrandLogo variant="mark" className="h-14 w-14 object-contain" />
          <Button type="button" variant="secondary" size="icon" onClick={onClose} aria-label="Close gallery navigation">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid gap-6">
          <Link to="/dashboard" className="inline-flex w-fit items-center gap-2 text-sm text-ink transition hover:text-ink/70" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Link>
          <h1 className="break-words text-3xl font-medium tracking-normal text-ink">{title}</h1>
        </div>
        <nav className="grid gap-4 text-left text-2xl font-normal">
          <GalleryNavButton active={activeSection === 'setup'} onClick={() => onSectionChange('setup')}>
            Gallery Setup
          </GalleryNavButton>
          <GalleryNavButton active={activeSection === 'photos'} onClick={() => onSectionChange('photos')}>
            Photos & Media
          </GalleryNavButton>
          <GalleryNavButton active={activeSection === 'scenes'} onClick={() => onSectionChange('scenes')}>
            Scenes
          </GalleryNavButton>
          <GalleryNavButton active={activeSection === 'proofing'} onClick={() => onSectionChange('proofing')}>
            Client Proofing
          </GalleryNavButton>
          <GalleryNavButton active={activeSection === 'share'} onClick={() => onSectionChange('share')}>
            Share Gallery
          </GalleryNavButton>
          <GalleryNavButton active={activeSection === 'statistics'} onClick={() => onSectionChange('statistics')}>
            Gallery Statistics
          </GalleryNavButton>
        </nav>
        <div className="mt-auto">
          {shareUrl ? (
            <Button asChild className="w-full">
              <a href={shareUrl} target="_blank" rel="noreferrer">
                Preview Gallery
              </a>
            </Button>
          ) : (
            <Button disabled className="w-full">
              Preview Gallery
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function GalleryNavButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      className={`relative w-fit border-l-2 pl-4 text-left transition ${
        active
          ? 'border-ink text-ink font-medium'
          : 'border-transparent text-ink/70 hover:border-ink/25 hover:text-ink'
      }`}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
    >
      {children}
    </button>
  );
}

function LoadingSection({ label }: { label: string }) {
  return (
    <div className="flex min-h-64 items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-sea" aria-label={label} />
    </div>
  );
}

function ShareGallerySection({ gallery, shareUrl }: { gallery: Gallery; shareUrl: string }) {
  const { token } = useAuth();
  const [copied, setCopied] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const emailMutation = useMutation({
    mutationFn: (input: { recipientEmail: string; passcode: string; customMessage?: string }) =>
      shareGalleryByEmail(token!, gallery.galleryId, input),
    onSuccess: (_result, input) => {
      setSentTo(input.recipientEmail);
      setPasscode('');
      setCustomMessage('');
    }
  });

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function handleCopy() {
    if (!shareUrl) {
      return;
    }
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  }

  return (
    <section className="grid max-w-3xl gap-6">
      <div>
        <h2 className="text-2xl font-medium tracking-normal text-ink">Share Gallery</h2>
        <p className="mt-2 text-sm text-ink/70">Share this public gallery link with the client.</p>
      </div>
      <div className="grid gap-3 rounded-sm border border-ink/10 bg-bone/45 p-4">
        <Field label="Gallery link">
          <Input value={shareUrl} readOnly />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void handleCopy()} disabled={!shareUrl}>
            {copied ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
            <span>{copied ? 'Copied' : 'Copy link'}</span>
          </Button>
          {copied ? <span className="inline-flex h-10 items-center text-sm font-medium text-ink/70" aria-live="polite">Link copied</span> : null}
          {shareUrl ? (
            <Button asChild variant="secondary">
              <a href={shareUrl} target="_blank" rel="noreferrer">
                Preview Gallery
              </a>
            </Button>
          ) : null}
        </div>
      </div>
      <div className="grid gap-4 rounded-sm border border-ink/10 bg-bone/45 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sea/10 text-sea">
            <Mail className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-medium text-ink">Share via email</h3>
            <p className="mt-1 text-sm text-ink/70">Send the gallery link and passcode in a beautifully formatted email.</p>
          </div>
        </div>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSentTo('');
            emailMutation.mutate({ recipientEmail: recipientEmail.trim(), passcode, customMessage: customMessage.trim() || undefined });
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Client email address">
              <Input
                type="email"
                value={recipientEmail}
                autoComplete="email"
                placeholder="client@example.com"
                required
                onChange={(event) => setRecipientEmail(event.target.value)}
              />
            </Field>
            <Field label="Current gallery passcode">
              <Input
                type="password"
                value={passcode}
                minLength={4}
                autoComplete="off"
                required
                onChange={(event) => setPasscode(event.target.value)}
              />
            </Field>
          </div>

          <Field label="Optional custom message" hint="Appears in the email header, above 'Your private gallery is ready...'">
            <Textarea
              value={customMessage}
              placeholder="Write a short personal message to the client"
              rows={3}
              onChange={(event) => setCustomMessage(event.target.value)}
            />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={emailMutation.isPending || gallery.isArchived}>
              {emailMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span>{emailMutation.isPending ? 'Sending' : 'Send email'}</span>
            </Button>
            {sentTo ? <p className="text-sm font-medium text-sea" aria-live="polite">Email sent to {sentTo}</p> : null}
          </div>
          {gallery.isArchived ? <p className="text-sm text-clay">Activate this gallery before sharing it by email.</p> : null}
          {emailMutation.error ? <p className="text-sm text-clay">{emailMutation.error.message}</p> : null}
          <p className="text-xs text-ink/70">The passcode is included in the email but is never stored in readable form.</p>
        </form>
      </div>
    </section>
  );
}

function ScenesSection({
  scenes,
  gallery,
  photos,
  filteredPhotos,
  sceneFilter,
  selectedPhotoIds,
  selectedCount,
  selectionMode,
  assignmentScene,
  isSavingScenes,
  isAssigning,
  saveError,
  assignmentError,
  assignmentFeedback,
  onFilterChange,
  onAssignmentSceneChange,
  onStartSelection,
  onCancelSelection,
  onSelectAllVisible,
  onClearSelection,
  onToggleSelected,
  onAssignScene,
  onClearScene,
  onSaveScenes,
  onMediaError
}: {
  scenes: string[];
  gallery: Gallery;
  photos: Photo[];
  filteredPhotos: Photo[];
  sceneFilter: string;
  selectedPhotoIds: Set<string>;
  selectedCount: number;
  selectionMode: boolean;
  assignmentScene: string;
  isSavingScenes: boolean;
  isAssigning: boolean;
  saveError: string | null;
  assignmentError: Error | null;
  assignmentFeedback: string | null;
  onFilterChange: (filter: string) => void;
  onAssignmentSceneChange: (scene: string) => void;
  onStartSelection: () => void;
  onCancelSelection: () => void;
  onSelectAllVisible: () => void;
  onClearSelection: () => void;
  onToggleSelected: (photoId: string) => void;
  onAssignScene: () => void;
  onClearScene: () => void;
  onSaveScenes: (nextScenes: string[]) => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [sceneDraft, setSceneDraft] = useState('');
  const [draggedScene, setDraggedScene] = useState<string | null>(null);
  const [dragOverScene, setDragOverScene] = useState<string | null>(null);
  const sceneOptions = [
    ...scenes.map((scene) => ({ value: scene, label: scene, count: filterPhotosByScene(photos, scene).length })),
    { value: UNASSIGNED_SCENE, label: 'Unassigned', count: filterPhotosByScene(photos, UNASSIGNED_SCENE).length }
  ];
  const activeSceneOption = sceneOptions.find((option) => option.value === sceneFilter);
  const showingLabel = activeSceneOption?.label ?? 'Unassigned';
  const showingCount = filteredPhotos.length;
  const showAssignmentControls = selectedCount > 0;

  useEffect(() => {
    setSceneDraft('');
    setDraggedScene(null);
    setDragOverScene(null);
  }, [gallery.galleryId, gallery.updatedAt]);

  function submitScenes(nextScenes: string[]) {
    if (sameStringArray(nextScenes, scenes)) {
      return;
    }
    onSaveScenes(nextScenes);
  }

  function addScene() {
    const normalized = sceneDraft.trim().replace(/\s+/g, ' ');
    if (!normalized || scenes.some((scene) => scene.toLowerCase() === normalized.toLowerCase())) {
      setSceneDraft('');
      return;
    }
    submitScenes([...scenes, normalized]);
    setSceneDraft('');
  }

  function removeScene(sceneToRemove: string) {
    submitScenes(scenes.filter((scene) => scene !== sceneToRemove));
  }

  function moveScene(sceneToMove: string, targetScene: string) {
    if (sceneToMove === targetScene) {
      return;
    }
    const fromIndex = scenes.indexOf(sceneToMove);
    const toIndex = scenes.indexOf(targetScene);
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    const next = [...scenes];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    submitScenes(next);
  }

  function handleSceneDragStart(event: DragEvent<HTMLSpanElement>, scene: string) {
    setDraggedScene(scene);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', scene);
  }

  function handleSceneDrop(event: DragEvent<HTMLSpanElement>, scene: string) {
    event.preventDefault();
    const sceneToMove = draggedScene ?? event.dataTransfer.getData('text/plain');
    if (sceneToMove) {
      moveScene(sceneToMove, scene);
    }
    setDraggedScene(null);
    setDragOverScene(null);
  }

  return (
    <section className="grid gap-6">
      <div className="grid gap-4 rounded-sm border border-ink/10 bg-bone/55 p-4">
        <div>
          <h2 className="text-2xl font-medium tracking-normal text-ink">Scenes</h2>
          <p className="mt-2 text-sm text-ink/70">Organize your scene list and assign loaded photos without affecting gallery setup settings.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={sceneDraft}
            onChange={(event) => setSceneDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addScene();
              }
            }}
            maxLength={40}
            placeholder="Getting ready"
            disabled={isSavingScenes}
          />
          <Button type="button" variant="secondary" onClick={addScene} disabled={isSavingScenes}>
            {isSavingScenes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span>Add scene</span>
          </Button>
        </div>
        {scenes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {scenes.map((scene) => (
              <span
                key={scene}
                draggable={!isSavingScenes}
                className={`inline-flex h-8 items-center gap-1 rounded-sm bg-white px-2 text-sm font-medium text-ink shadow-sm ring-1 transition ${
                  isSavingScenes ? 'cursor-not-allowed opacity-70' : 'cursor-grab active:cursor-grabbing'
                } ${dragOverScene === scene && draggedScene !== scene ? 'ring-2 ring-sea' : 'ring-ink/10'}`}
                onDragStart={(event) => handleSceneDragStart(event, scene)}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (!isSavingScenes) {
                    event.dataTransfer.dropEffect = 'move';
                    setDragOverScene(scene);
                  }
                }}
                onDragLeave={() => setDragOverScene((current) => (current === scene ? null : current))}
                onDrop={(event) => !isSavingScenes && handleSceneDrop(event, scene)}
                onDragEnd={() => {
                  setDraggedScene(null);
                  setDragOverScene(null);
                }}
                title={isSavingScenes ? 'Saving scenes' : 'Drag to reorder'}
              >
                <GripVertical className="h-3.5 w-3.5 text-ink/70" aria-hidden="true" />
                {scene}
                <button type="button" className="rounded p-1 text-ink/70 hover:bg-smoke hover:text-ink disabled:cursor-not-allowed" onClick={() => removeScene(scene)} disabled={isSavingScenes} aria-label={`Remove ${scene}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink/70">No named scenes yet. Add one to start organizing the gallery.</p>
        )}
        <div className="min-h-5">
          {isSavingScenes ? <p className="text-sm text-ink/70">Saving scenes…</p> : null}
          {saveError ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{saveError}</p> : null}
        </div>
      </div>

      <section className="grid gap-4 rounded-sm border border-ink/10 bg-white p-4 shadow-sm">
        <div className="grid gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink">Assign photos</h3>
            <p className="mt-1 text-sm text-ink/70">Choose what you are viewing, select photos from that set, then assign them to a scene.</p>
          </div>
          <div className="grid gap-2">
            <p className="text-sm font-medium text-ink">View scene</p>
            <div className="flex flex-wrap gap-2">
            {sceneOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onFilterChange(option.value)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${
                  sceneFilter === option.value
                    ? 'border-sea bg-sea/10 font-medium text-sea'
                    : 'border-ink/10 bg-bone text-ink hover:border-ink/20 hover:bg-smoke'
                }`}
              >
                <span>{option.label}</span>
                <span className={sceneFilter === option.value ? 'text-sea/70' : 'text-ink/60'}>{option.count}</span>
              </button>
            ))}
            </div>
          </div>
          <p className="text-sm text-ink/70">
            {sceneFilter === UNASSIGNED_SCENE
              ? `Showing ${showingCount} ${showingCount === 1 ? 'unassigned photo' : 'unassigned photos'}`
              : `Showing ${showingCount} ${showingCount === 1 ? 'photo' : 'photos'} in ${showingLabel}`}
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-sm border border-ink/10 bg-bone/55 p-3">
          <div className="flex flex-wrap items-center gap-2">
            {selectionMode ? (
              <>
                <span className="rounded-sm bg-sea/10 px-2.5 py-1 text-xs font-medium text-sea">{selectedCount} selected</span>
                <Button type="button" variant="secondary" size="sm" onClick={onClearSelection} disabled={selectedCount === 0}>
                  Clear selection
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={onCancelSelection}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button type="button" variant="secondary" size="sm" onClick={onStartSelection} disabled={filteredPhotos.length === 0}>
                Start selecting
              </Button>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={onSelectAllVisible} disabled={filteredPhotos.length === 0}>
              Select all in this scene
            </Button>
          </div>

          {showAssignmentControls ? (
            <div className="grid gap-2 sm:grid-cols-[auto_minmax(12rem,1fr)] lg:grid-cols-[auto_minmax(12rem,1fr)_auto_auto] lg:items-center">
              <label htmlFor="scene-assignment" className="text-sm font-medium text-ink">
                Assign selected to
              </label>
              <select
                id="scene-assignment"
                className="h-10 min-w-0 rounded-sm border border-ink/20 bg-ivory px-3 text-sm text-ink outline-none transition focus:border-black focus:ring-4 focus:ring-black/10"
                value={assignmentScene}
                onChange={(event) => onAssignmentSceneChange(event.target.value)}
                disabled={scenes.length === 0}
                aria-label="Scene to assign"
              >
                <option value="">Choose scene</option>
                {scenes.map((scene) => (
                  <option key={scene} value={scene}>
                    {scene}
                  </option>
                ))}
              </select>
              <Button type="button" size="sm" className="w-full whitespace-nowrap px-3 lg:w-auto" onClick={onAssignScene} disabled={!assignmentScene || isAssigning}>
                {isAssigning ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span>Assign to scene</span>
              </Button>
              <Button type="button" variant="secondary" size="sm" className="w-full whitespace-nowrap px-3 lg:w-auto" onClick={onClearScene} disabled={isAssigning}>
                Remove from scene
              </Button>
            </div>
          ) : null}
        </div>

        {assignmentError ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{assignmentError.message}</p> : null}
        {assignmentFeedback ? <p className="rounded-sm bg-sea/10 p-3 text-sm text-sea">{assignmentFeedback}</p> : null}

        {filteredPhotos.length === 0 ? (
          <SceneEmptyState sceneFilter={sceneFilter} showAddMedia={false} isUploading={false} onFilesSelected={() => undefined} />
        ) : (
          <GalleryGrid
            photos={filteredPhotos}
            selectedPhotoIds={selectionMode ? selectedPhotoIds : undefined}
            onToggleSelected={selectionMode ? onToggleSelected : undefined}
            onMediaError={onMediaError}
          />
        )}
      </section>
    </section>
  );
}

function SceneEmptyState({
  sceneFilter,
  showAddMedia,
  isUploading,
  onFilesSelected
}: {
  sceneFilter: string;
  showAddMedia: boolean;
  isUploading: boolean;
  onFilesSelected: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const message =
    sceneFilter === UNASSIGNED_SCENE
      ? 'No loaded photos are unassigned.'
      : sceneFilter === ''
        ? 'No photos yet.'
      : sceneFilter === ALL_SCENES
        ? 'No photos yet.'
        : `No loaded photos are assigned to ${sceneFilter}.`;

  return (
    <div className="flex min-h-48 flex-col items-center justify-center rounded-sm border border-dashed border-ink/20 bg-white/60 p-8 text-center">
      <ImageOff className="mb-3 h-8 w-8 text-ink/70" />
      <p className="font-medium text-ink">{message}</p>
      {showAddMedia ? (
        <label className="mt-5 inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-sm px-4 text-sm font-medium text-ink/70 transition hover:text-ink">
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          <span>{isUploading ? 'Uploading' : 'Add Media'}</span>
          <input
            className="sr-only"
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,.mp4"
            multiple
            onChange={onFilesSelected}
            disabled={isUploading}
          />
        </label>
      ) : null}
    </div>
  );
}

function FavouriteSelectionControls({
  selections,
  selectedClientKey,
  isLoading,
  isReopening,
  isExporting,
  exportJob,
  error,
  onFilterChange,
  onReopen,
  onExport
}: {
  selections: FavouriteSelection[];
  selectedClientKey: string;
  isLoading: boolean;
  isReopening: boolean;
  isExporting: boolean;
  exportJob?: ExportJob;
  error: Error | null;
  onFilterChange: (clientKey: string) => void;
  onReopen: (clientKey: string) => void;
  onExport: (clientKey: string) => void;
}) {
  const selected = selections.find((selection) => selection.clientKey === selectedClientKey);
  return (
    <section className="grid gap-4 rounded-sm border border-ink/10 bg-white p-4 shadow-sm" aria-labelledby="client-selections-heading">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 id="client-selections-heading" className="text-sm font-semibold text-ink">Client proofing selection</h2>
          <p className="mt-1 text-sm text-ink/70">Filter independently from scenes, reopen a client’s selection, or prepare clean originals.</p>
        </div>
        <select
          className="h-10 min-w-0 max-w-full rounded-sm border border-ink/15 bg-white px-3 text-sm text-ink outline-none transition focus:border-sea focus:ring-4 focus:ring-sea/15 lg:max-w-xl"
          value={selectedClientKey}
          onChange={(event) => onFilterChange(event.target.value)}
          aria-label="Filter photos by client selection"
          disabled={isLoading}
        >
          <option value={ALL_CLIENTS}>All photos</option>
          {selections.map((selection) => (
            <option key={selection.clientKey} value={selection.clientKey}>
              {selection.clientName} — {selection.clientEmail} — {selection.count} — {selection.status === 'SUBMITTED' ? 'Submitted' : 'Reopened'} — {formatSelectionTime(selection.submittedAt)}
            </option>
          ))}
        </select>
      </div>

      {selected ? (
        <div className="grid gap-3 rounded-sm border border-ink/10 bg-bone/55 p-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div>
            <p className="font-medium text-ink">{selected.clientName} <span className="font-normal text-ink/70">· {selected.clientEmail}</span></p>
            <p className="mt-1 text-sm text-ink/70">
              {selected.count} {selected.count === 1 ? 'photo' : 'photos'} · {selected.status === 'SUBMITTED' ? 'Submitted' : 'Reopened'} · {formatSelectionTime(selected.submittedAt)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <Button type="button" variant="secondary" size="sm" onClick={() => onReopen(selected.clientKey)} disabled={selected.status !== 'SUBMITTED' || isReopening}>
              {isReopening ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Reopen
            </Button>
            <Button type="button" size="sm" onClick={() => onExport(selected.clientKey)} disabled={selected.status !== 'SUBMITTED' || selected.count === 0 || isExporting}>
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Full-quality ZIP
            </Button>
          </div>
        </div>
      ) : selections.length === 0 && !isLoading ? (
        <p className="rounded-sm bg-bone/55 p-3 text-sm text-ink/70">No clients have submitted a selection yet.</p>
      ) : null}

      {exportJob ? (
        <div className="rounded-sm bg-sea/10 p-3 text-sm text-ink">
          {exportJob.status === 'READY' ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Full-quality selection ready:</span>
              {exportJob.parts.map((part) => part.url ? (
                <Button key={part.partIndex} asChild variant="secondary" size="sm">
                  <a href={part.url}>Download part {part.partIndex + 1}</a>
                </Button>
              ) : null)}
            </div>
          ) : exportJob.status === 'FAILED' ? (
            <p className="text-clay">The ZIP could not be prepared. Refresh the selection and try again.</p>
          ) : (
            <p className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Preparing full-quality ZIP…</p>
          )}
        </div>
      ) : null}
  {error ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{error.message}</p> : null}
    </section>
  );
}

function ProofingSection({
  gallery,
  scenes,
  photos,
  filteredPhotos,
  selections,
  selectedClientKey,
  selectedSelection,
  sceneFilter,
  isSaving,
  isLoadingSelections,
  isReopening,
  isExporting,
  exportJob,
  error,
  onSave,
  onClientChange,
  onSceneFilterChange,
  onReopen,
  onExport,
  onMediaError
}: {
  gallery: Gallery;
  scenes: string[];
  photos: Photo[];
  filteredPhotos: Photo[];
  selections: FavouriteSelection[];
  selectedClientKey: string;
  selectedSelection?: FavouriteSelection;
  sceneFilter: string;
  isSaving: boolean;
  isLoadingSelections: boolean;
  isReopening: boolean;
  isExporting: boolean;
  exportJob?: ExportJob;
  error: Error | null;
  onSave: (favourites: { limit: number } | null) => void;
  onClientChange: (clientKey: string) => void;
  onSceneFilterChange: (filter: string) => void;
  onReopen: (clientKey: string) => void;
  onExport: (clientKey: string) => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [favouritesEnabled, setFavouritesEnabled] = useState(Boolean(gallery.favourites));
  const [favouritesLimit, setFavouritesLimit] = useState(gallery.favourites?.limit ?? 20);
  const hasChanges =
    favouritesEnabled !== Boolean(gallery.favourites) ||
    (favouritesEnabled && favouritesLimit !== (gallery.favourites?.limit ?? 20));
  const sceneOptions = [
    { value: ALL_SCENES, label: 'All scenes', count: selectedSelection ? selectedSelection.photoIds.length : 0 },
    { value: UNASSIGNED_SCENE, label: 'Unassigned', count: selectedSelection ? filterPhotosByScene(photos.filter((photo) => selectedSelection.photoIds.includes(photo.photoId)), UNASSIGNED_SCENE).length : 0 },
    ...scenes.map((scene) => ({
      value: scene,
      label: scene,
      count: selectedSelection ? filterPhotosByScene(photos.filter((photo) => selectedSelection.photoIds.includes(photo.photoId)), scene).length : 0
    }))
  ];

  useEffect(() => {
    setFavouritesEnabled(Boolean(gallery.favourites));
    setFavouritesLimit(gallery.favourites?.limit ?? 20);
  }, [gallery.galleryId, gallery.updatedAt]);

  function handleSave() {
    const limit = Math.max(1, Math.min(100, Number.isFinite(favouritesLimit) ? favouritesLimit : 20));
    onSave(favouritesEnabled ? { limit } : null);
  }

  return (
    <section className="grid gap-6">
      <div>
        <h2 className="text-2xl font-medium tracking-normal text-ink">Client Proofing</h2>
        <p className="mt-2 text-sm text-ink/70">Control proofing limits and review submitted client selections without changing gallery media.</p>
      </div>

      <section className="grid gap-4 rounded-sm border border-ink/10 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-ink">Proofing settings</h3>
            <p className="mt-1 text-sm text-ink/70">Let each client submit one proofing selection using their name and email.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={favouritesEnabled}
            aria-label="Enable client favourites"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink"
            onClick={() =>
              setFavouritesEnabled((current) => {
                const enabled = !current;
                if (enabled && !gallery.favourites) setFavouritesLimit(20);
                return enabled;
              })
            }
          >
            <span>{favouritesEnabled ? 'Enabled' : 'Disabled'}</span>
            <span className={`relative h-6 w-11 rounded-full transition ${favouritesEnabled ? 'bg-sea' : 'bg-ink/20'}`} aria-hidden="true">
              <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${favouritesEnabled ? 'left-6' : 'left-1'}`} />
            </span>
          </button>
        </div>
        {favouritesEnabled ? (
          <div className="max-w-xs">
            <Field label="Photos per client" hint="Choose a limit from 1 to 100.">
              <Input
                type="number"
                min={1}
                max={100}
                step={1}
                value={favouritesLimit}
                required
                onChange={(event) => setFavouritesLimit(Math.max(1, Math.min(100, Number(event.target.value) || 1)))}
              />
            </Field>
          </div>
        ) : (
          <p className="text-xs text-ink/70">Disabling proofing keeps past selections visible here until you reopen or export them.</p>
        )}
        <div className="flex min-h-10 items-center justify-end">
          {hasChanges ? (
            <Button type="button" disabled={isSaving} onClick={handleSave}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <span>{isSaving ? 'Saving' : 'Save proofing settings'}</span>
            </Button>
          ) : null}
        </div>
      </section>

      <FavouriteSelectionControls
        selections={selections}
        selectedClientKey={selectedClientKey}
        isLoading={isLoadingSelections}
        isReopening={isReopening}
        isExporting={isExporting}
        exportJob={exportJob}
        error={error}
        onFilterChange={onClientChange}
        onReopen={onReopen}
        onExport={onExport}
      />

      {selectedSelection ? (
        <section className="grid gap-4">
          <div className="flex flex-col gap-3 rounded-sm border border-ink/10 bg-white p-4 shadow-sm xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-ink">Selected photos</h3>
              <p className="mt-1 text-sm text-ink/70">Review only the submitted favourites for this client, optionally filtered by scene.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {sceneOptions.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={sceneFilter === option.value ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onSceneFilterChange(option.value)}
                >
                  <span>{option.label}</span>
                  <span className={sceneFilter === option.value ? 'text-white/70' : 'text-ink/70'}>{option.count}</span>
                </Button>
              ))}
            </div>
          </div>
          {filteredPhotos.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center rounded-sm border border-dashed border-ink/20 bg-white/60 p-8 text-center">
              <ImageOff className="mb-3 h-8 w-8 text-ink/70" />
              <p className="font-medium text-ink">No selected photos match this scene filter.</p>
            </div>
          ) : (
            <GalleryGrid photos={filteredPhotos} onMediaError={onMediaError} />
          )}
        </section>
      ) : null}
    </section>
  );
}

function formatSelectionTime(value: string): string {
  return new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function filterPhotosByScene(photos: Photo[], sceneFilter: string): Photo[] {
  if (sceneFilter === ALL_SCENES) {
    return photos;
  }
  if (sceneFilter === UNASSIGNED_SCENE) {
    return photos.filter((photo) => !photo.scene);
  }
  return photos.filter((photo) => photo.scene === sceneFilter);
}

function GallerySettings({
  gallery,
  photos,
  videos,
  watermarks,
  isSaving,
  isUpdatingCover,
  coverError,
  showSaveConfirmation,
  error,
  watermarkError,
  isManagingWatermarks,
  onDirtyChange,
  onChooseCover,
  onSettingsChange,
  onSave,
  onUploadWatermark,
  onRenameWatermark,
  onDeleteWatermark,
  onRetryWatermark,
  onMediaError
}: {
  gallery: Gallery;
  photos: Photo[];
  videos: Video[];
  watermarks: WatermarkAsset[];
  isSaving: boolean;
  isUpdatingCover: boolean;
  coverError: Error | null;
  showSaveConfirmation: boolean;
  error: Error | null;
  watermarkError: Error | null;
  isManagingWatermarks: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onChooseCover: () => void;
  onSettingsChange: () => void;
  onSave: (input: {
    title: string;
    slug: string;
    description: string | null;
    coverFont: CoverFont;
    customisation: { colorScheme: GalleryColorScheme; galleryStyle: GalleryStyle };
    eventDate: string | null;
    passcode?: string;
    isArchived: boolean;
    watermark: GalleryWatermarkConfig | null;
  }) => void;
  onUploadWatermark: (name: string, file: File) => void;
  onRenameWatermark: (watermarkId: string, name: string) => void;
  onDeleteWatermark: (watermarkId: string) => void;
  onRetryWatermark: () => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [title, setTitle] = useState(gallery.title);
  const [slug, setSlug] = useState(gallery.gallerySlug);
  const [description, setDescription] = useState(gallery.description ?? '');
  const [coverFont, setCoverFont] = useState<CoverFont>(() => normalizeCoverFont(gallery.coverFont));
  const [colorScheme, setColorScheme] = useState<GalleryColorScheme>(() =>
    normalizeGalleryColorScheme(gallery.customisation?.colorScheme)
  );
  const [galleryStyle, setGalleryStyle] = useState<GalleryStyle>(() =>
    normalizeGalleryStyle(gallery.customisation?.galleryStyle)
  );
  const [eventDate, setEventDate] = useState<string | null>(gallery.eventDate ?? null);
  const [passcode, setPasscode] = useState('');
  const [eventDatePickerOpen, setEventDatePickerOpen] = useState(false);
  const [active, setActive] = useState(!gallery.isArchived);
  const [selectedWatermarkId, setSelectedWatermarkId] = useState(gallery.watermarkConfig?.watermarkId ?? '');
  const [watermarkPosition, setWatermarkPosition] = useState<WatermarkPosition>(gallery.watermarkConfig?.position ?? DEFAULT_WATERMARK_POSITION);
  const [watermarkSize, setWatermarkSize] = useState(gallery.watermarkConfig?.sizePercent ?? DEFAULT_WATERMARK_SIZE);
  const [watermarkOpacity, setWatermarkOpacity] = useState(gallery.watermarkConfig?.opacityPercent ?? DEFAULT_WATERMARK_OPACITY);
  const [watermarkName, setWatermarkName] = useState('');
  const [isAddWatermarkOpen, setIsAddWatermarkOpen] = useState(false);
  const watermarkUploadDisabled = isManagingWatermarks || !watermarkName.trim();
  const hasWatermarks = watermarks.length > 0;
  const selectedWatermark = watermarks.find((asset) => asset.watermarkId === selectedWatermarkId);
  const coverMedia = getCoverMedia(gallery, photos, videos);
  const shouldShowWatermarkStatus =
    gallery.watermarkStatus === 'PROCESSING' ||
    gallery.watermarkStatus === 'ACTIVE' ||
    gallery.watermarkStatus === 'FAILED';
  const showRemoveWatermark = Boolean(gallery.watermarkConfig?.watermarkId);
  const previousWatermarkCount = useRef(watermarks.length);
  const nextWatermark: GalleryWatermarkConfig | null = selectedWatermarkId
    ? { watermarkId: selectedWatermarkId, position: watermarkPosition, sizePercent: watermarkSize, opacityPercent: watermarkOpacity, marginPercent: 3 }
    : null;
  const hasChanges =
    title !== gallery.title ||
    slug !== gallery.gallerySlug ||
    description !== (gallery.description ?? '') ||
    coverFont !== normalizeCoverFont(gallery.coverFont) ||
    colorScheme !== normalizeGalleryColorScheme(gallery.customisation?.colorScheme) ||
    galleryStyle !== normalizeGalleryStyle(gallery.customisation?.galleryStyle) ||
    eventDate !== (gallery.eventDate ?? null) ||
    passcode.trim() !== '' ||
    active !== !gallery.isArchived ||
    !sameWatermark(nextWatermark, gallery.watermarkConfig);

  useEffect(() => {
    setTitle(gallery.title);
    setSlug(gallery.gallerySlug);
    setDescription(gallery.description ?? '');
    setCoverFont(normalizeCoverFont(gallery.coverFont));
    setColorScheme(normalizeGalleryColorScheme(gallery.customisation?.colorScheme));
    setGalleryStyle(normalizeGalleryStyle(gallery.customisation?.galleryStyle));
    setEventDate(gallery.eventDate ?? null);
    setPasscode('');
    setActive(!gallery.isArchived);
    setSelectedWatermarkId(gallery.watermarkConfig?.watermarkId ?? '');
    setWatermarkPosition(gallery.watermarkConfig?.position ?? DEFAULT_WATERMARK_POSITION);
    setWatermarkSize(gallery.watermarkConfig?.sizePercent ?? DEFAULT_WATERMARK_SIZE);
    setWatermarkOpacity(gallery.watermarkConfig?.opacityPercent ?? DEFAULT_WATERMARK_OPACITY);
    setWatermarkName('');
    setIsAddWatermarkOpen(false);
  }, [gallery.galleryId, gallery.updatedAt]);

  useEffect(() => {
    onDirtyChange(hasChanges);
    return () => onDirtyChange(false);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    if (isAddWatermarkOpen && watermarks.length > previousWatermarkCount.current) {
      setWatermarkName('');
      setIsAddWatermarkOpen(false);
    }
    previousWatermarkCount.current = watermarks.length;
  }, [isAddWatermarkOpen, watermarks.length]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSettingsChange();
    const nextSlug = slugify(slug || title) || gallery.gallerySlug;
    onSave({
      title,
      slug: nextSlug,
      description: description.trim() || null,
      coverFont,
      customisation: { colorScheme, galleryStyle },
      eventDate,
      passcode: passcode.trim() || undefined,
      isArchived: !active,
      watermark: nextWatermark
    });
  }

  return (
    <section className="grid gap-8">
      <form id="gallery-settings-form" className="grid gap-5" onSubmit={handleSubmit}>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input
              value={title}
              onChange={(event) => {
                onSettingsChange();
                setTitle(event.target.value);
              }}
              required
            />
          </Field>
          <Field label="Custom Link">
            <Input
              value={slug}
              onChange={(event) => {
                onSettingsChange();
                setSlug(slugInputValue(event.target.value));
              }}
              required
            />
          </Field>
        </div>

        <Field label="Description">
          <Textarea
            value={description}
            onChange={(event) => {
              onSettingsChange();
              setDescription(event.target.value);
            }}
            placeholder="A short note for the gallery cover"
          />
        </Field>

        <Field label="Event date" hint="Optional">
          <button
            type="button"
            className="flex h-11 w-full items-center justify-between rounded-sm border border-ink/20 bg-ivory px-3.5 text-sm text-ink outline-none transition focus:border-black focus:ring-4 focus:ring-black/10"
            onClick={() => setEventDatePickerOpen(true)}
          >
            <span className={eventDate ? 'text-ink' : 'text-ink/70'}>{eventDate ? formatDateOnlyLong(eventDate) : 'Select a date'}</span>
            <CalendarDays className="h-4 w-4 text-ink/60" />
          </button>
        </Field>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="grid min-h-[5.75rem] grid-rows-[auto_2.75rem_auto] gap-1">
            <span className="text-sm font-medium text-ink">New passcode</span>
            <Input
              value={passcode}
              onChange={(event) => {
                onSettingsChange();
                setPasscode(event.target.value);
              }}
              minLength={4}
            />
            <span className="text-sm text-ink/70">Leave blank to keep the current passcode.</span>
          </div>
          <div className="grid min-h-[5.75rem] grid-rows-[auto_2.75rem_auto] gap-1">
            <span className="text-sm font-medium text-ink">Availability</span>
            <label className="flex h-11 items-center justify-between gap-3 rounded-sm border border-ink/20 bg-ivory px-3.5">
              <span className="text-sm text-ink/70">Public gallery link is active</span>
              <input
                className="h-4 w-4 accent-ink"
                type="checkbox"
                checked={active}
                onChange={(event) => {
                  onSettingsChange();
                  setActive(event.target.checked);
                }}
              />
            </label>
            <span aria-hidden="true" className="text-sm text-transparent">Alignment helper</span>
          </div>
        </div>

        <section className="grid gap-5 rounded-sm border border-ink/10 bg-bone/55 p-4 sm:p-5" aria-labelledby="gallery-customisation-heading">
          <div>
            <h3 id="gallery-customisation-heading" className="text-base font-semibold text-ink">Customisation</h3>
            <p className="mt-1 text-sm text-ink/70">Shape the look of this client gallery. More customisation options will be added here over time.</p>
          </div>

          <div className="grid gap-3 rounded-sm border border-ink/10 bg-bone/55 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-ink">Gallery cover</h3>
                <p className="mt-1 text-sm text-ink/70">
                  {gallery.coverPhotoId ? 'Cover media is selected.' : 'Choose a cover from any uploaded photo or video.'}
                </p>
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={onChooseCover} disabled={isUpdatingCover}>
                {isUpdatingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                <span>Choose cover</span>
              </Button>
            </div>
            {coverMedia ? (
              <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-start">
                <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-smoke ring-1 ring-ink/10">
                  <img
                    src={coverMedia.previewUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    style={coverFocalPointStyle(gallery.coverFocalPoint)}
                    onLoad={handleMediaLoad}
                    onError={(event) => handleMediaError(event, onMediaError)}
                  />
                  {coverMedia.kind === 'video' ? (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/92 px-2.5 py-1 text-xs font-medium text-ink shadow-soft">
                      <Play className="h-3.5 w-3.5 fill-current" />
                      Video
                    </span>
                  ) : null}
                </div>
                <div className="grid gap-1">
                  <p className="text-sm font-medium text-ink">{coverMedia.fileName}</p>
                  <p className="text-sm text-ink/70">{coverMedia.kind === 'video' ? 'Current cover uses a video poster preview.' : 'Current cover uses the photo preview.'}</p>
                </div>
              </div>
            ) : (
              <div className="flex min-h-28 items-center justify-center rounded-sm border border-dashed border-ink/15 bg-white/65 p-4 text-center">
                <div>
                  <ImageOff className="mx-auto h-6 w-6 text-ink/55" />
                  <p className="mt-2 text-sm font-medium text-ink">No cover selected</p>
                  <p className="mt-1 text-sm text-ink/70">Pick a photo or video poster from your uploaded media.</p>
                </div>
              </div>
            )}
            {coverError ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{coverError.message}</p> : null}
          </div>

          <fieldset className="grid gap-2">
          <legend className="text-sm font-semibold text-ink">Cover font</legend>
          <p id="cover-font-hint" className="text-sm text-ink/70">Choose the typeface used for the gallery name and description on the cover.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8" aria-describedby="cover-font-hint">
            {COVER_FONT_OPTIONS.map((option) => {
              const selected = coverFont === option.value;
              return (
                <label key={option.value} className="relative min-w-0 cursor-pointer">
                  <input
                    type="radio"
                    name="cover-font"
                    value={option.value}
                    checked={selected}
                    onChange={() => {
                      onSettingsChange();
                      setCoverFont(option.value);
                    }}
                    className="peer sr-only"
                    aria-label={option.label}
                  />
                  <span
                    className={`grid h-full min-w-0 overflow-hidden rounded-sm border bg-ivory transition ${
                      selected
                        ? 'border-ink ring-1 ring-ink'
                        : 'border-ink/15 hover:border-ink/40'
                    } peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-black`}
                  >
                    <span className="flex min-h-20 min-w-0 items-center justify-center overflow-hidden bg-white/65 px-2 py-3 text-center text-lg leading-tight text-ink">
                      <span
                        data-testid={`cover-font-preview-${option.value}`}
                        className="line-clamp-2 max-w-full break-words"
                        style={coverFontPreviewStyle(option.value)}
                      >
                        {title.trim() || gallery.title}
                      </span>
                    </span>
                    <span className={`border-t px-2 py-2 text-center text-[11px] leading-tight ${selected ? 'border-ink/20 font-semibold text-ink' : 'border-ink/10 text-ink/70'}`}>
                      {option.label}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          </fieldset>

          <ColorSchemePicker
            value={colorScheme}
            name="gallery-colour-scheme"
            onChange={(value) => {
              onSettingsChange();
              setColorScheme(value);
            }}
          />

          <fieldset className="grid gap-2">
            <legend className="text-sm font-semibold text-ink">Gallery style</legend>
            <p id="gallery-style-hint" className="text-sm text-ink/70">Choose a cover composition. Filmstrip also changes the unlocked photo story.</p>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-describedby="gallery-style-hint">
              {GALLERY_STYLE_OPTIONS.map((option) => {
                const selected = galleryStyle === option.id;
                return (
                  <label key={option.id} className="relative min-w-0 cursor-pointer">
                    <input
                      type="radio"
                      name="gallery-style"
                      value={option.id}
                      checked={selected}
                      onChange={() => {
                        onSettingsChange();
                        setGalleryStyle(option.id);
                      }}
                      className="peer sr-only"
                      aria-label={option.name}
                      aria-describedby={option.experimental ? `gallery-style-${option.id}-status` : undefined}
                    />
                    <span className={`grid h-full overflow-hidden rounded-sm border bg-white transition ${
                      selected ? 'border-ink ring-1 ring-ink' : 'border-ink/15 hover:border-ink/40'
                    } peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-black`}>
                      <GalleryStyleMiniature
                        galleryStyle={option.id}
                        title={title.trim() || gallery.title}
                        coverUrl={coverMedia?.previewUrl}
                        colorScheme={colorScheme}
                        focalPoint={gallery.coverFocalPoint}
                      />
                      <span className="grid gap-0.5 border-t border-ink/10 px-3 py-2.5">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className={`text-sm ${selected ? 'font-semibold text-ink' : 'font-medium text-ink/80'}`}>{option.name}</span>
                          {option.experimental ? (
                            <span
                              id={`gallery-style-${option.id}-status`}
                              className="rounded-full border border-clay/25 bg-clay/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-clay"
                            >
                              Experimental
                            </span>
                          ) : null}
                        </span>
                        <span className="text-xs leading-snug text-ink/60">{option.description}</span>
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <GalleryStylePreview
            galleryStyle={galleryStyle}
            colorScheme={colorScheme}
            coverFont={coverFont}
            title={title.trim() || gallery.title}
            description={description.trim()}
            eventDate={eventDate}
            coverUrl={coverMedia?.previewUrl}
            focalPoint={gallery.coverFocalPoint}
            photos={galleryPreviewPhotos(coverMedia, photos, videos)}
          />
        </section>

        <div className="grid gap-5 rounded-sm border border-ink/10 bg-bone/55 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-ink">Gallery watermark</h3>
              <p className="mt-1 text-sm text-ink/70">Protect client-facing photos while keeping originals clean in your workspace.</p>
            </div>
            {gallery.watermarkQuota ? (
              <p className="text-xs text-ink/70">
                {gallery.watermarkQuota.remaining} of {gallery.watermarkQuota.limit} changes left · resets{' '}
                {formatWatermarkReset(gallery.watermarkQuota.resetAt)}
              </p>
            ) : null}
          </div>

          {shouldShowWatermarkStatus ? (
            <div className={`rounded-sm p-3 text-sm ${gallery.watermarkStatus === 'FAILED' ? 'bg-clay/10 text-clay' : 'bg-sea/10 text-sea'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p>
                  {gallery.watermarkStatus === 'PROCESSING'
                    ? `Preparing protected photos (${gallery.watermarkReadyCount ?? 0}/${gallery.watermarkTargetCount ?? gallery.imageCount ?? 0})`
                    : gallery.watermarkStatus === 'ACTIVE'
                      ? 'Watermark protection is active'
                      : gallery.watermarkFailureMessage ?? 'Watermark processing failed'}
                </p>
                {gallery.watermarkStatus === 'FAILED' ? (
                  <Button type="button" size="sm" variant="secondary" onClick={onRetryWatermark}>
                    <RotateCcw className="h-4 w-4" /> Retry
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] lg:items-start">
            <div className="grid gap-4">
              <section className="grid gap-3 rounded-sm border border-ink/10 bg-white/70 p-4" aria-labelledby="watermark-step-choose">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p id="watermark-step-choose" className="text-sm font-semibold text-ink">Choose watermark</p>
                    <p className="mt-1 text-sm text-ink/70">
                      {hasWatermarks
                        ? 'Pick a saved watermark to preview in this gallery. The choice stays draft-only until you save settings.'
                        : 'Create a reusable watermark first, then choose it for this gallery when you are ready.'}
                    </p>
                  </div>
                  {selectedWatermark ? (
                    <span className="rounded-full bg-sea/10 px-2.5 py-1 text-xs font-medium text-sea">Selected</span>
                  ) : null}
                </div>
                {hasWatermarks ? (
                  <WatermarkLibrary
                    watermarks={watermarks}
                    selectedId={selectedWatermarkId}
                    activeId={gallery.watermarkConfig?.watermarkId}
                    disabled={isManagingWatermarks}
                    onSelect={(watermarkId) => {
                      onSettingsChange();
                      setSelectedWatermarkId(watermarkId);
                    }}
                    onRename={onRenameWatermark}
                    onDelete={onDeleteWatermark}
                    onMediaError={onMediaError}
                  />
                ) : null}
                {!isAddWatermarkOpen ? (
                  <Button type="button" variant="secondary" size="sm" className="w-fit" onClick={() => setIsAddWatermarkOpen(true)}>
                    <Plus className="h-4 w-4" />
                    <span>Add watermark</span>
                  </Button>
                ) : null}
              </section>

              {isAddWatermarkOpen ? (
                <section className="grid gap-3 rounded-sm border border-dashed border-ink/15 bg-white/75 p-4" aria-labelledby="watermark-step-add">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p id="watermark-step-add" className="text-sm font-semibold text-ink">Add watermark</p>
                      <p className="mt-1 text-sm text-ink/70">Name the watermark first, then upload a transparent PNG for your reusable library.</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setWatermarkName('');
                        setIsAddWatermarkOpen(false);
                      }}
                      disabled={isManagingWatermarks}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                    <Field label="Watermark name (required)" hint="Enter a name to enable Upload PNG.">
                      <Input
                        value={watermarkName}
                        maxLength={80}
                        placeholder="e.g. White logo"
                        aria-required="true"
                        onChange={(event) => setWatermarkName(event.target.value)}
                      />
                    </Field>
                    <label
                      aria-disabled={watermarkUploadDisabled}
                      className={`inline-flex h-10 items-center justify-center gap-2 rounded-sm px-4 text-sm font-medium text-white transition ${
                        watermarkUploadDisabled ? 'cursor-not-allowed bg-ink/35' : 'cursor-pointer bg-ink hover:bg-ink/90'
                      }`}
                    >
                      {isManagingWatermarks ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      Upload PNG
                      <input
                        className="sr-only"
                        type="file"
                        accept="image/png,.png"
                        disabled={watermarkUploadDisabled}
                        aria-describedby="watermark-upload-requirements"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) onUploadWatermark(watermarkName.trim(), file);
                          event.target.value = '';
                        }}
                      />
                    </label>
                    <p id="watermark-upload-requirements" className="text-xs text-ink/70 sm:col-span-2">
                      Transparent PNG up to 5 MiB, between 64 and 4096px.
                    </p>
                  </div>
                </section>
              ) : null}

              <section className="grid gap-4 rounded-sm border border-ink/10 bg-white/70 p-4" aria-labelledby="watermark-step-adjust">
                <div>
                  <p id="watermark-step-adjust" className="text-sm font-semibold text-ink">Adjust appearance</p>
                  <p className="mt-1 text-sm text-ink/70">Place the watermark and fine-tune how large and visible it should be on protected photos.</p>
                </div>
                {selectedWatermark ? (
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <span className="text-sm font-medium text-ink">Position</span>
                      <div className="grid grid-cols-3 gap-2" aria-label="Watermark position">
                        {WATERMARK_POSITIONS.map((position) => (
                          <button
                            key={position}
                            type="button"
                            className={`grid min-h-16 place-items-center rounded-sm border px-2 py-3 text-center text-xs font-medium transition ${
                              watermarkPosition === position
                                ? 'border-sea bg-sea/10 text-sea ring-2 ring-sea/15'
                                : 'border-ink/15 bg-white text-ink/70 hover:border-ink/30'
                            }`}
                            onClick={() => {
                              onSettingsChange();
                              setWatermarkPosition(position);
                            }}
                            aria-label={WATERMARK_POSITION_LABELS[position]}
                          >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-current text-[10px]">
                              ●
                            </span>
                            <span>{WATERMARK_POSITION_LABELS[position]}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label={`Size · ${watermarkSize}% of image width`}>
                        <input className="w-full accent-sea" type="range" min={5} max={40} value={watermarkSize} onChange={(event) => {
                          onSettingsChange();
                          setWatermarkSize(Number(event.target.value));
                        }} />
                      </Field>
                      <Field label={`Opacity · ${watermarkOpacity}%`}>
                        <input className="w-full accent-sea" type="range" min={10} max={100} value={watermarkOpacity} onChange={(event) => {
                          onSettingsChange();
                          setWatermarkOpacity(Number(event.target.value));
                        }} />
                      </Field>
                    </div>
                  </div>
                ) : (
                  <p className="rounded-sm border border-dashed border-ink/15 bg-bone/40 p-3 text-sm text-ink/70">
                    Choose a watermark first to unlock position, size, and opacity controls.
                  </p>
                )}

                <div className="lg:hidden">
                  <WatermarkPreview
                    backgroundUrl={gallery.coverUrl ?? photos.find((photo) => photo.url)?.url}
                    watermark={selectedWatermark}
                    position={watermarkPosition}
                    sizePercent={watermarkSize}
                    opacityPercent={watermarkOpacity}
                    onMediaError={onMediaError}
                  />
                </div>
              </section>
            </div>

            <div className="hidden lg:block">
              <WatermarkPreview
                backgroundUrl={gallery.coverUrl ?? photos.find((photo) => photo.url)?.url}
                watermark={selectedWatermark}
                position={watermarkPosition}
                sizePercent={watermarkSize}
                opacityPercent={watermarkOpacity}
                onMediaError={onMediaError}
              />
            </div>
          </div>

          {showRemoveWatermark ? (
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => {
              onSettingsChange();
              setSelectedWatermarkId('');
            }}>
              Remove watermark
            </Button>
          ) : null}
          {watermarkError ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{watermarkError.message}</p> : null}
        </div>
        {error ? <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">{error.message}</p> : null}

        <div className="flex min-h-10 items-center justify-end">
          {showSaveConfirmation ? (
            <p className="inline-flex items-center gap-2 text-sm font-medium text-ink/70" role="status" aria-live="polite">
              <Check className="h-4 w-4" aria-hidden="true" />
              <span>Settings saved</span>
            </p>
          ) : hasChanges ? (
            <Button disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <span>{isSaving ? 'Saving' : 'Save settings'}</span>
            </Button>
          ) : null}
        </div>
      </form>
      <EventDatePickerDialog
        open={eventDatePickerOpen}
        value={eventDate}
        onOpenChange={setEventDatePickerOpen}
        onChange={(value) => {
          onSettingsChange();
          setEventDate(value);
        }}
      />
    </section>
  );
}

function GalleryStyleMiniature({
  galleryStyle,
  title,
  coverUrl,
  colorScheme,
  focalPoint
}: {
  galleryStyle: GalleryStyle;
  title: string;
  coverUrl?: string;
  colorScheme: GalleryColorScheme;
  focalPoint?: CoverFocalPoint;
}) {
  const scheme = galleryColorSchemeOf(colorScheme);
  const miniatureStyle = {
    '--client-gallery-background': scheme.backgroundColor,
    '--client-gallery-accent': scheme.accentColor,
    '--client-gallery-accent-text': scheme.accentTextColor
  } as CSSProperties;

  return (
    <span
      className="gallery-style-miniature"
      data-gallery-style={galleryStyle}
      style={miniatureStyle}
      aria-hidden="true"
    >
      <span className="gallery-style-miniature-cover">
        {coverUrl ? <img src={coverUrl} alt="" style={coverFocalPointStyle(focalPoint)} /> : <span className="gallery-style-media-fallback" />}
        <span className="gallery-style-miniature-title">{title}</span>
      </span>
    </span>
  );
}

function GalleryStylePreview({
  galleryStyle,
  colorScheme,
  coverFont,
  title,
  description,
  eventDate,
  coverUrl,
  focalPoint,
  photos
}: {
  galleryStyle: GalleryStyle;
  colorScheme: GalleryColorScheme;
  coverFont: CoverFont;
  title: string;
  description: string;
  eventDate: string | null;
  coverUrl?: string;
  focalPoint?: CoverFocalPoint;
  photos: Photo[];
}) {
  const [viewport, setViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [state, setState] = useState<'locked' | 'unlocked'>('locked');
  const [scale, setScale] = useState(1);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const scheme = galleryColorSchemeOf(colorScheme);
  const previewStyle = {
    '--client-gallery-background': scheme.backgroundColor,
    '--client-gallery-accent': scheme.accentColor,
    '--client-gallery-accent-text': scheme.accentTextColor,
    '--client-gallery-ink': '#2D2D2D',
    '--gallery-preview-viewport-height': viewport === 'desktop' ? '900px' : '844px'
  } as CSSProperties;

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const updateScale = () => {
      const canonicalWidth = viewport === 'desktop' ? 1440 : 390;
      setScale(Math.min(1, Math.max(0.5, stage.clientWidth / canonicalWidth)));
    };
    updateScale();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScale);
    observer?.observe(stage);
    return () => observer?.disconnect();
  }, [viewport]);

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;
    if (typeof preview.scrollTo === 'function') {
      preview.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else {
      preview.scrollTop = 0;
      preview.scrollLeft = 0;
    }
  }, [viewport, state, galleryStyle]);

  const canonicalWidth = viewport === 'desktop' ? 1440 : 390;
  const canonicalHeight = viewport === 'desktop' ? 900 : 844;

  return (
    <section className="grid gap-3 border-t border-ink/10 pt-5" aria-label="Selected gallery style preview">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-ink">Live style preview</h4>
          <p className="mt-1 text-sm text-ink/70">Preview the unsaved cover and opening layout. Example controls are visual only.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-sm bg-smoke p-1" aria-label="Preview viewport">
            <PreviewToggle ariaLabel="Preview Desktop" active={viewport === 'desktop'} onClick={() => setViewport('desktop')}>
              <Monitor className="h-4 w-4" /> Desktop
            </PreviewToggle>
            <PreviewToggle ariaLabel="Preview Mobile" active={viewport === 'mobile'} onClick={() => setViewport('mobile')}>
              <Smartphone className="h-4 w-4" /> Mobile
            </PreviewToggle>
          </div>
          <div className="inline-flex rounded-sm bg-smoke p-1" aria-label="Preview gallery state">
            <PreviewToggle active={state === 'locked'} onClick={() => setState('locked')}>Locked</PreviewToggle>
            <PreviewToggle active={state === 'unlocked'} onClick={() => setState('unlocked')}>Unlocked</PreviewToggle>
          </div>
        </div>
      </div>

      <div ref={stageRef} className="gallery-style-preview-shell flex justify-center rounded-sm bg-smoke/80 p-2 sm:p-4">
        <div className="gallery-style-preview-frame" style={{ width: canonicalWidth * scale, height: canonicalHeight * scale }}>
          <div
            ref={previewRef}
            className={`gallery-style-preview client-gallery overflow-y-auto bg-white shadow-soft ${viewport === 'desktop' ? 'gallery-style-preview-desktop' : 'gallery-style-preview-mobile'}`}
            data-testid="gallery-style-preview"
            data-preview-viewport={viewport}
            data-gallery-style={galleryStyle}
            style={{ ...previewStyle, '--preview-cover-height': `${canonicalHeight}px`, width: canonicalWidth, height: canonicalHeight, transform: `scale(${scale})` } as CSSProperties}
          >
          <div className="pointer-events-none select-none" aria-hidden="true" inert>
            <GalleryCover
              title={title}
              description={description || undefined}
              eventDate={eventDate}
              coverFont={coverFont}
              galleryStyle={galleryStyle}
              state={state}
              imageUrl={coverUrl}
              focalPoint={focalPoint}
              action={state === 'locked' ? <PreviewPasscodeCard /> : (
                <span className="gallery-hero-cta gallery-hero-cta-unlocked">View our story</span>
              )}
            />
            {state === 'unlocked' ? (
              <div className="gallery-style-preview-story client-gallery-surface" data-gallery-style={galleryStoryStyleOf(galleryStyle)}>
                <div className="gallery-style-preview-band">Our story</div>
                <div className="gallery-style-preview-grid" aria-hidden="true" />
                <GalleryGrid photos={photos} galleryStyle={galleryStyle} variant="client" showPhotoDetails={false} />
              </div>
            ) : null}
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PreviewToggle({ active, onClick, children, ariaLabel }: { active: boolean; onClick: () => void; children: ReactNode; ariaLabel?: string }) {
  return (
    <button
      type="button"
      className={`inline-flex min-h-10 items-center gap-2 rounded-sm px-3 text-sm font-medium ${active ? 'bg-white text-ink shadow-sm' : 'text-ink/65'}`}
      aria-pressed={active}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function PreviewPasscodeCard() {
  return (
    <div className="client-gallery-passcode-card mx-auto grid w-full max-w-sm gap-4 rounded-sm border p-5 text-left shadow-soft sm:p-6" data-passcode-card>
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-sm bg-smoke/80 text-ink/70">
        <Lock className="h-4 w-4" data-preview-lock-icon />
      </div>
      <div className="grid gap-1">
        <span className="text-sm font-medium text-ink">Passcode</span>
        <span className="h-11 rounded-sm border border-ink/20 bg-ivory" />
      </div>
      <span className="client-gallery-button flex min-h-10 items-center justify-center gap-2 rounded-sm px-4 text-sm font-medium" data-gallery-button-variant="primary">
        <Lock className="h-4 w-4" />
        Unlock
      </span>
    </div>
  );
}

function galleryPreviewPhotos(
  coverMedia: ReturnType<typeof getCoverMedia>,
  photos: Photo[],
  videos: Video[]
): Photo[] {
  const result = photos.slice(0, 7);
  const used = new Set(result.map((photo) => photo.url).filter(Boolean));
  const fallbackUrls = [coverMedia?.previewUrl, ...videos.map((video) => video.posterUrl)].filter(
    (url): url is string => Boolean(url) && !used.has(url)
  );
  fallbackUrls.slice(0, Math.max(0, 7 - result.length)).forEach((url, index) => {
    result.push({
      photoId: `preview-fallback-${index}`,
      galleryId: 'preview',
      fileName: 'Preview image',
      contentType: 'image/jpeg',
      sizeBytes: 0,
      width: 4,
      height: 3,
      previewWidth: 4,
      previewHeight: 3,
      createdAt: '',
      url,
      mediaType: 'image'
    });
  });
  while (result.length < 7) {
    const index = result.length;
    result.push({
      photoId: `preview-placeholder-${index}`,
      galleryId: 'preview',
      fileName: 'Preview image',
      contentType: 'image/jpeg',
      sizeBytes: 0,
      width: 4,
      height: 3,
      previewWidth: 4,
      previewHeight: 3,
      createdAt: '',
      mediaType: 'image'
    });
  }
  return result;
}

function GalleryCoverPicker({
  open,
  gallery,
  photos,
  videos,
  isSaving,
  error,
  onClose,
  onSelectCover,
  onClearCover,
  onMediaError
}: {
  open: boolean;
  gallery: Gallery;
  photos: Photo[];
  videos: Video[];
  isSaving: boolean;
  error: Error | null;
  onClose: () => void;
  onSelectCover: (photoId: string, focalPoint: CoverFocalPoint) => void;
  onClearCover: () => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [draft, setDraft] = useState<{ item: CoverMediaItem; focalPoint: CoverFocalPoint } | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>(() => window.innerWidth < 640 ? 'mobile' : 'desktop');

  useEffect(() => {
    if (!open) {
      setDraft(null);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !isSaving) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSaving, onClose, open]);

  if (!open) {
    return null;
  }

  const mediaItems = buildCoverMediaItems(photos, videos);
  const dialogTitle = draft ? 'Position gallery cover' : 'Choose gallery cover';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/55 p-4" role="dialog" aria-modal="true" aria-label="Choose gallery cover">
      <button type="button" className="absolute inset-0" aria-label="Close cover picker overlay" onClick={() => !isSaving && onClose()} />
      <div className="relative z-10 flex max-h-[min(88vh,900px)] w-full max-w-5xl flex-col overflow-hidden rounded-sm bg-white shadow-2xl ring-1 ring-ink/10">
        <div className="flex items-start justify-between gap-4 border-b border-ink/10 px-5 py-4 sm:px-6">
          <div>
            <h3 className="text-lg font-medium text-ink">{dialogTitle}</h3>
            <p className="mt-1 text-sm text-ink/70">
              {draft ? 'Drag the image to keep the important area in view, then save.' : 'Select a photo or video poster to position before saving.'}
            </p>
          </div>
          <Button type="button" variant="secondary" size="icon" onClick={onClose} disabled={isSaving} aria-label="Close cover picker">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {!draft ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 bg-bone/40 px-5 py-3 sm:px-6">
            <p className="text-sm text-ink/70">
              {gallery.coverPhotoId ? 'Current cover is marked below.' : 'No cover is currently selected.'}
            </p>
            <Button type="button" variant="secondary" size="sm" onClick={onClearCover} disabled={isSaving || !gallery.coverPhotoId}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageOff className="h-4 w-4" />}
              <span>Clear cover</span>
            </Button>
          </div>
        ) : null}
        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          {error ? <p className="mb-4 rounded-sm bg-clay/10 p-3 text-sm text-clay">{error.message}</p> : null}
          {draft ? (
            <CoverPositionEditor
              gallery={gallery}
              item={draft.item}
              focalPoint={draft.focalPoint}
              previewMode={previewMode}
              disabled={isSaving}
              onFocalPointChange={(focalPoint) => setDraft((current) => current ? { ...current, focalPoint } : current)}
              onPreviewModeChange={setPreviewMode}
              onMediaError={onMediaError}
            />
          ) : mediaItems.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center rounded-sm border border-dashed border-ink/20 bg-smoke/40 p-8 text-center">
              <ImageOff className="h-8 w-8 text-ink/55" />
              <p className="mt-3 font-medium text-ink">No media available</p>
              <p className="mt-1 max-w-sm text-sm text-ink/70">Upload photos or videos in Photos & Media, then come back to choose a cover.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {mediaItems.map((item) => {
                const isCurrent = item.photoId === gallery.coverPhotoId;
                return (
                  <button
                    key={item.photoId}
                    type="button"
                    className={`group relative overflow-hidden rounded-sm bg-smoke text-left ring-1 transition ${
                      isCurrent ? 'ring-2 ring-sea' : 'ring-ink/10 hover:ring-ink/25'
                    }`}
                    onClick={() => setDraft({
                      item,
                      focalPoint: isCurrent ? normalizeCoverFocalPoint(gallery.coverFocalPoint) : { ...CENTER_COVER_FOCAL_POINT }
                    })}
                    disabled={isSaving}
                    aria-pressed={isCurrent}
                    aria-label={isCurrent ? `Adjust crop for ${item.fileName}, current cover` : `Select ${item.fileName} as cover`}
                  >
                    <div className="relative aspect-[4/3]">
                      <img
                        src={item.previewUrl}
                        alt={item.fileName}
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                        loading="lazy"
                        onLoad={handleMediaLoad}
                        onError={(event) => handleMediaError(event, onMediaError)}
                      />
                      <span className="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent" />
                      <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-white/92 px-2 py-1 text-[11px] font-medium text-ink shadow-soft">
                        {item.kind === 'video' ? <VideoIcon className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                        {item.kind === 'video' ? 'Video' : 'Photo'}
                      </span>
                      {item.kind === 'video' ? (
                        <span className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/92 text-ink shadow-soft">
                          <Play className="ml-0.5 h-4 w-4 fill-current" />
                        </span>
                      ) : null}
                      {isCurrent ? (
                        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-sea px-2.5 py-1 text-[11px] font-medium text-white shadow-soft">
                          <Check className="h-3.5 w-3.5" />
                          Current cover
                        </span>
                      ) : null}
                    </div>
                    <div className="p-3">
                      <p className="truncate text-sm font-medium text-ink">{item.fileName}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {draft ? (
          <div className="flex flex-col-reverse gap-3 border-t border-ink/10 bg-bone/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <Button type="button" variant="secondary" onClick={() => setDraft(null)} disabled={isSaving}>
              <ArrowLeft className="h-4 w-4" />
              <span>Back to covers</span>
            </Button>
            <Button type="button" onClick={() => onSelectCover(draft.item.photoId, draft.focalPoint)} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              <span>{isSaving ? 'Saving cover' : 'Save cover'}</span>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

type CoverMediaItem = ReturnType<typeof buildCoverMediaItems>[number];

function CoverPositionEditor({
  gallery,
  item,
  focalPoint,
  previewMode,
  disabled,
  onFocalPointChange,
  onPreviewModeChange,
  onMediaError
}: {
  gallery: Gallery;
  item: CoverMediaItem;
  focalPoint: CoverFocalPoint;
  previewMode: 'desktop' | 'mobile';
  disabled: boolean;
  onFocalPointChange: (focalPoint: CoverFocalPoint) => void;
  onPreviewModeChange: (mode: 'desktop' | 'mobile') => void;
  onMediaError?: MediaErrorHandler;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const dragStart = useRef<{ pointerId: number; x: number; y: number; focalPoint: CoverFocalPoint } | null>(null);

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (disabled) return;
    dragStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, focalPoint };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const start = dragStart.current;
    const frame = frameRef.current;
    const image = imageRef.current;
    if (!start || start.pointerId !== event.pointerId || !frame || !image) return;
    const bounds = frame.getBoundingClientRect();
    onFocalPointChange(dragCoverFocalPoint(
      start.focalPoint,
      { x: event.clientX - start.x, y: event.clientY - start.y },
      { width: bounds.width, height: bounds.height },
      { width: image.naturalWidth, height: image.naturalHeight }
    ));
  }

  function stopDragging(event: ReactPointerEvent<HTMLDivElement>) {
    if (dragStart.current?.pointerId === event.pointerId) {
      dragStart.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (disabled || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    onFocalPointChange(nudgeCoverFocalPoint(
      focalPoint,
      event.key as 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown',
      event.shiftKey ? 5 : 1
    ));
  }

  return (
    <div className="grid gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-ink">{item.fileName}</p>
          <p id="cover-position-help" className="mt-1 text-sm text-ink/70">Drag to reposition. Use arrow keys for fine adjustments, or Shift + arrow for larger steps.</p>
        </div>
        <div className="inline-flex self-start rounded-sm bg-smoke p-1" aria-label="Cover preview size">
          <button
            type="button"
            className={`inline-flex h-10 items-center gap-2 rounded-sm px-3 text-sm font-medium ${previewMode === 'desktop' ? 'bg-white text-ink shadow-sm' : 'text-ink/65'}`}
            aria-pressed={previewMode === 'desktop'}
            onClick={() => onPreviewModeChange('desktop')}
          >
            <Monitor className="h-4 w-4" /> Desktop
          </button>
          <button
            type="button"
            className={`inline-flex h-10 items-center gap-2 rounded-sm px-3 text-sm font-medium ${previewMode === 'mobile' ? 'bg-white text-ink shadow-sm' : 'text-ink/65'}`}
            aria-pressed={previewMode === 'mobile'}
            onClick={() => onPreviewModeChange('mobile')}
          >
            <Smartphone className="h-4 w-4" /> Mobile
          </button>
        </div>
      </div>

      <div className="flex justify-center rounded-sm bg-smoke/70 p-3 sm:p-5">
        <div
          ref={frameRef}
          data-testid="cover-crop-preview"
          data-cover-font={normalizeCoverFont(gallery.coverFont)}
          className={`relative w-full select-none overflow-hidden bg-ink text-white outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-ink ${
            previewMode === 'desktop' ? 'aspect-[21/10] max-w-4xl' : 'aspect-[9/14] max-w-[300px]'
          } ${disabled ? 'cursor-wait' : 'cursor-grab touch-none active:cursor-grabbing'}`}
          style={coverFontVariables(normalizeCoverFont(gallery.coverFont))}
          tabIndex={disabled ? -1 : 0}
          aria-label="Cover crop preview"
          aria-describedby="cover-position-help cover-position-value"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
          onKeyDown={handleKeyDown}
        >
          <img
            ref={imageRef}
            src={item.previewUrl}
            alt=""
            draggable={false}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={coverFocalPointStyle(focalPoint)}
            onLoad={handleMediaLoad}
            onError={(event) => handleMediaError(event, onMediaError)}
          />
          <div className="pointer-events-none absolute inset-0 bg-ink/20" />
          <div className="pointer-events-none relative z-10 mx-auto flex h-full w-full max-w-5xl flex-col items-center justify-center px-4 text-center sm:px-6">
            <h4 className={`gallery-hero-title max-w-full break-words leading-none text-white ${previewMode === 'desktop' ? 'text-3xl sm:text-5xl' : 'text-3xl'}`}>
              {gallery.title}
            </h4>
            {gallery.eventDate ? <p className="mt-3 text-xs font-medium text-white/85 sm:text-sm">{formatDateOnlyLong(gallery.eventDate)}</p> : null}
            {gallery.description ? <p className="gallery-hero-description mt-4 line-clamp-3 text-xs text-white/90 sm:text-sm">{gallery.description}</p> : null}
          </div>
          <span className="pointer-events-none absolute bottom-3 left-3 z-20 inline-flex items-center gap-1.5 rounded-sm bg-ink/70 px-2 py-1 text-xs text-white/90">
            <Move className="h-3.5 w-3.5" /> Drag to position
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-sm border border-ink/10 bg-bone/40 p-3 sm:flex-row sm:items-center sm:justify-between">
        <p id="cover-position-value" className="text-sm text-ink/70" aria-live="polite">
          Horizontal {Math.round(focalPoint.x)}% · Vertical {Math.round(focalPoint.y)}%
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onFocalPointChange({ ...CENTER_COVER_FOCAL_POINT })}
          disabled={disabled || (focalPoint.x === 50 && focalPoint.y === 50)}
        >
          <RotateCcw className="h-4 w-4" />
          <span>Reset to centre</span>
        </Button>
      </div>
    </div>
  );
}

function sameStringArray(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function sameWatermark(left: GalleryWatermarkConfig | null, right?: GalleryWatermarkConfig): boolean {
  if (!left || !right) return !left && !right;
  return left.watermarkId === right.watermarkId &&
    left.position === right.position &&
    left.sizePercent === right.sizePercent &&
    left.opacityPercent === right.opacityPercent;
}

function getCoverMedia(gallery: Gallery, photos: Photo[], videos: Video[]) {
  if (!gallery.coverPhotoId) {
    return null;
  }
  const video = videos.find((item) => item.photoId === gallery.coverPhotoId);
  if (video) {
    return { kind: 'video' as const, fileName: video.fileName, previewUrl: video.posterUrl };
  }
  const photo = photos.find((item) => item.photoId === gallery.coverPhotoId);
  if (photo?.url) {
    return { kind: 'photo' as const, fileName: photo.fileName, previewUrl: photo.url };
  }
  return null;
}

function buildCoverMediaItems(photos: Photo[], videos: Video[]) {
  return [
    ...videos
      .filter((video) => Boolean(video.posterUrl))
      .map((video) => ({ photoId: video.photoId, fileName: video.fileName, previewUrl: video.posterUrl, kind: 'video' as const })),
    ...photos
      .filter((photo) => Boolean(photo.url))
      .map((photo) => ({ photoId: photo.photoId, fileName: photo.fileName, previewUrl: photo.url!, kind: 'photo' as const })),
  ];
}

function WatermarkLibrary({
  watermarks,
  selectedId,
  activeId,
  disabled,
  onSelect,
  onRename,
  onDelete,
  onMediaError
}: {
  watermarks: WatermarkAsset[];
  selectedId: string;
  activeId?: string;
  disabled: boolean;
  onSelect: (watermarkId: string) => void;
  onRename: (watermarkId: string, name: string) => void;
  onDelete: (watermarkId: string) => void;
  onMediaError?: MediaErrorHandler;
}) {
  if (watermarks.length === 0) {
    return null;
  }
  return (
    <div className="grid gap-2" aria-label="Watermark library">
      {watermarks.map((asset) => (
        <WatermarkLibraryRow
          key={asset.watermarkId}
          asset={asset}
          selected={selectedId === asset.watermarkId}
          inUse={activeId === asset.watermarkId}
          disabled={disabled}
          onSelect={() => onSelect(asset.watermarkId)}
          onRename={(name) => onRename(asset.watermarkId, name)}
          onDelete={() => onDelete(asset.watermarkId)}
          onMediaError={onMediaError}
        />
      ))}
    </div>
  );
}

function WatermarkLibraryRow({
  asset,
  selected,
  inUse,
  disabled,
  onSelect,
  onRename,
  onDelete,
  onMediaError
}: {
  asset: WatermarkAsset;
  selected: boolean;
  inUse: boolean;
  disabled: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [name, setName] = useState(asset.name);
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => setName(asset.name), [asset.name]);
  useEffect(() => {
    if (!isEditing) {
      setName(asset.name);
    }
  }, [asset.name, isEditing]);

  function commitRename() {
    const normalized = name.trim();
    setIsEditing(false);
    if (normalized && normalized !== asset.name) {
      onRename(normalized);
      return;
    }
    setName(asset.name);
  }

  return (
    <div className={`rounded-sm border bg-white p-2 transition ${selected ? 'border-sea ring-2 ring-sea/15' : 'border-ink/10'}`}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
        <button
          type="button"
          className="grid min-w-0 grid-cols-[56px_minmax(0,1fr)] items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-bone/45"
          onClick={onSelect}
          disabled={disabled}
          aria-pressed={selected}
          aria-label={`Select ${asset.name}`}
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-sm bg-smoke p-1 ring-1 ring-ink/10">
            <img
              src={asset.url}
              alt=""
              className="max-h-full max-w-full object-contain"
              onLoad={handleMediaLoad}
              onError={(event) => handleMediaError(event, onMediaError)}
            />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-medium text-ink">{asset.name}</p>
              {selected ? <span className="rounded-full bg-sea/10 px-2 py-0.5 text-[11px] font-medium text-sea">Selected</span> : null}
              {inUse ? <span className="rounded-full bg-ink/8 px-2 py-0.5 text-[11px] font-medium text-ink/75">Active</span> : null}
            </div>
            <p className="mt-1 text-xs text-ink/60">Select this watermark for the current gallery draft.</p>
          </div>
        </button>
        <div className="flex items-start gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            onClick={() => setIsEditing(true)}
            aria-label={`Edit ${asset.name}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" disabled={disabled || inUse} onClick={onDelete} aria-label={`Delete ${asset.name}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {isEditing ? (
        <div className="mt-2 border-t border-ink/10 pt-2">
          <Input
            value={name}
            maxLength={80}
            aria-label={`Rename ${asset.name}`}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitRename();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                setName(asset.name);
                setIsEditing(false);
              }
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function WatermarkPreview({
  backgroundUrl,
  watermark,
  position,
  sizePercent,
  opacityPercent,
  onMediaError
}: {
  backgroundUrl?: string;
  watermark?: WatermarkAsset;
  position: WatermarkPosition;
  sizePercent: number;
  opacityPercent: number;
  onMediaError?: MediaErrorHandler;
}) {
  const style = watermarkPreviewStyle(position, sizePercent, opacityPercent);
  return (
    <div className="grid gap-3 rounded-sm border border-ink/10 bg-white/70 p-4">
      <div>
        <p className="text-sm font-semibold text-ink">Live preview</p>
        <p className="mt-1 text-sm text-ink/70">Preview changes are free and only apply to client-facing photos after Save settings.</p>
      </div>
      <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-ink/85">
        {backgroundUrl ? (
          <img
            src={backgroundUrl}
            alt="Gallery watermark preview"
            className="h-full w-full object-cover"
            onLoad={handleMediaLoad}
            onError={(event) => handleMediaError(event, onMediaError)}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-white/55">Add a cover or photo to preview</div>
        )}
        {watermark ? (
          <img
            src={watermark.url}
            alt=""
            className="absolute h-auto object-contain"
            style={style}
            onLoad={handleMediaLoad}
            onError={(event) => handleMediaError(event, onMediaError)}
          />
        ) : null}
      </div>
    </div>
  );
}

function watermarkPreviewStyle(position: WatermarkPosition, sizePercent: number, opacityPercent: number): CSSProperties {
  const style: CSSProperties = { width: `${sizePercent}%`, opacity: opacityPercent / 100 };
  if (position.includes('left')) style.left = '3%';
  else if (position.includes('right')) style.right = '3%';
  else { style.left = '50%'; style.transform = 'translateX(-50%)'; }
  if (position.startsWith('top')) style.top = '3%';
  else if (position.startsWith('bottom')) style.bottom = '3%';
  else {
    style.top = '50%';
    style.transform = style.transform ? `${style.transform} translateY(-50%)` : 'translateY(-50%)';
  }
  return style;
}

function formatWatermarkReset(resetAt: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    hour: '2-digit',
    minute: '2-digit',
    day: 'numeric',
    month: 'short'
  }).format(new Date(resetAt));
}
